/**
 * Strikes a HYPERLINK field whole when the planned edits replace the link, so the
 * redline deletes the link the way Word deletes one with tracking on: its begin,
 * instruction, separate and end runs and its result runs each inside the author's
 * `w:del`, the instruction spelled `w:delInstrText`.
 *
 * CASES, by the card's instruction for a link the edits reach (`LinkInstruction`)
 *   - "keep"      -> never struck: the new words are edited in place inside the link
 *   - "change"    -> struck; the new words are written as a new HYPERLINK field to the new
 *                    address, inside the author's insertion (`hyperlinkFieldRunsAround`)
 *   - "needs_url" -> struck; the new words are written as plain text
 *   - none        -> struck only when the edits leave none of the words of its display
 *                    text (WHAT IS LEFT ALONE below), the new words then plain text
 *
 * WHY
 *   A HYPERLINK's display text is the author's own and is edited in place, the
 *   field kept (`fieldResultIsRedrawnByWord`). An edit that takes all of it left
 *   the four marker runs standing around an empty result: once accepted, an
 *   invisible link stayed in the document, and a replacement wrote its new words
 *   into the old link, pointing them at the old address. Struck whole, the link
 *   goes on Accept All and comes back, address and all, on Reject All, and the
 *   engine's edits, moved past the struck text, write new words as ordinary text
 *   beside the struck field.
 *
 * WHAT IS LEFT ALONE, for a link the card gave no instruction for
 *   A link one of whose content words the edits keep keeps its field. The planner
 *   replaces whole words, so a word counts as kept when the text the edits write
 *   over the link holds it, or holds it with an "s" or "es" added
 *   (`writesAContentWordBack`): "Fee" to "Fees" and "Schedule" to "Schedules" edit the
 *   link's words; "the Fee Schedule" to "the Annex B" replaces the link. A field
 *   Word redraws (REF, TOC, …) is the unlinker's (`WordOwnedTextUnlinker`), never
 *   struck here: the engine unlinks it first, on the edits as planned, so a link
 *   inside one leaves no empty outer field once struck.
 *
 * WHEN a link sits inside this author's own pending insertion
 *   CONSEQUENCE
 *     its runs are removed outright rather than struck, as the engine removes the
 *     author's own inserted text everywhere else.
 *
 * CASES, by how the paragraph spells the link
 *   - a complex HYPERLINK field     -> its runs struck, begin to end
 *   - a `w:fldSimple` HYPERLINK     -> rewritten in the complex spelling first (`rewriteAsComplexField`), since no
 *                                      `w:del` may hold a `w:fldSimple`, then struck the same way
 *   - a `w:hyperlink` element whose -> rewritten as the complex HYPERLINK field of the same address
 *     address is known                 (`rewriteHyperlinkElementAsComplexField`), then struck the same way: no
 *                                      `w:del` may hold a `w:hyperlink`, and an element left around struck runs
 *                                      stays in the document as an empty link once accepted
 *   - a `w:hyperlink` element whose -> the runs inside it struck: the element stays around runs that are all
 *     address is not known             deletions and holds no text once accepted
 *
 * WHEN the address is not known
 *   CASES
 *     - the caller had the paragraph alone, not its package (`TextChanges`): no relationships to read
 *     - the element's `r:id` names no hyperlink relationship of the package
 */
import { type HyperlinkTargetsByRelationshipId, hyperlinkElementAttributesOf } from "./hyperlink-targets";
import { fieldResultIsRedrawnByWord, fieldSimpleInstructionOf, elementNameWithoutPrefix, WORDPROCESSINGML_NS, XML_NS } from "./ooxml-names";
import { ParagraphLinks } from "./paragraph-links";
import type { ParagraphModel } from "./paragraph-model";
import { ParagraphRevisionMarks } from "./paragraph-revision-marks";
import type { Revision } from "./revision";
import { type AcceptedTextRange, type ComplexField, complexFieldWalkOf } from "./word-owned-text-unlinker";
import { type WordToken, WordTokenizer } from "../text/word-tokenizer";
import type { LinkInstruction } from "../wire/link-instruction";

/** The marker runs a complex field is written with around its result, each a fresh `w:r`. */
interface ComplexFieldMarkerRuns {
    /** The `fldChar begin` inside `beginRun`, where a simple field's `w:fldData` moves. */
    beginFieldChar: Element;
    beginRun: Element;
    instructionRun: Element;
    separateRun: Element;
    endRun: Element;
}

/**
 * The link the new words written where a struck link stood are written as, when the
 * card's instruction for the struck link is "change" (`LinkInstruction`).
 */
export interface ReplacementLinkOfStruckLink {
    /** The address the new link points to: the instruction's `newAddress`. */
    address: string;
    /** The struck link's first run of live text, whose formatting the new words take, so they read as a link reads. */
    formattingSourceRun: Element;
}

/** A stretch of display text a struck link held, in the offsets the edits were planned in, and its characters. */
export interface StruckDisplayText extends AcceptedTextRange {
    /** The characters of `startOffset..endOffset` before the strike: what the request may still want written. */
    displayText: string;
    /**
     * The link the words written back over this stretch are written as.
     *
     * NULL
     *   They are written as plain text: the instruction for the link was "needs_url", or the
     *   card gave none and the edits left no content word of the link.
     */
    replacementLink: ReplacementLinkOfStruckLink | null;
}

/** The bounds a struck link's display text had, for a link whose new words are written as a new link. */
interface StruckLinkReplacedByLink extends AcceptedTextRange {
    replacementLink: ReplacementLinkOfStruckLink;
}

/** One link the striker strikes whole: the display text it held, and what its new words are written as. */
interface StruckLink {
    struckRanges: StruckDisplayText[];
    /**
     * NULL
     *   The new words are plain text ("needs_url", or no instruction).
     */
    replacementLink: ReplacementLinkOfStruckLink | null;
    /**
     * The link's text from its first live character to its last.
     *
     * NULL
     *   Struck by its words, with no instruction: its new words are plain text, so no edit is matched to it.
     */
    linkBounds: AcceptedTextRange | null;
}

/** What the striker did to the paragraph, so the engine moves its planned edits past what it struck. */
export interface LinksStruckWhole {
    /** How many links were struck whole or, in the simple spelling, unwrapped. */
    linkCount: number;
    /** The display text the struck links held; it reads no more. */
    struckRanges: StruckDisplayText[];
    /** The struck links whose new words are written as a link ("change"), each with the bounds its display text had. */
    linksReplacedByLinks: StruckLinkReplacedByLink[];
}

/** Nothing struck: the paragraph is left as it came. */
const NO_LINK_STRUCK: LinksStruckWhole = { linkCount: 0, struckRanges: [], linksReplacedByLinks: [] };

/** One content word of a link's display text and where it stands in the accepted text. */
interface ContentWordAtOffset extends AcceptedTextRange {
    contentWord: string;
}

/** Where a link's live text starts in the accepted text, and the element that opens the link: its begin run, its `w:fldSimple` or its `w:hyperlink`. */
export interface LinkStart {
    startOffset: number;
    linkOpeningElement: Element;
}

/** Where a replacement's words are cut between two links (`wordCutBetweenLinks`), counted in its words. */
interface WordCutBetweenLinks {
    /** How many of its first words go to the first link's side of the stretch. */
    wordsForSideBeforeStretch: number;
    /** The index of its first word for the second link's side; the words between go over the stretch. */
    firstWordIndexAfterStretch: number;
}

/** One planned edit as the striker reads it: the accepted text it replaces and what it writes there. */
export interface EditOverAcceptedText extends AcceptedTextRange {
    replacementText: string;
}

/** One edit as the engine writes it once the striker ran: its words, and the link they are written as. */
export interface EditWritingWordsOrLink extends EditOverAcceptedText {
    /**
     * The new link the words are written inside (`ParagraphSplicer`).
     *
     * NULL
     *   Plain words, written where the edit stands.
     */
    replacementLink: ReplacementLinkOfStruckLink | null;
}

/**
 * The spacing an edit cut at a link's edge leaves as the page reads it: how long the
 * page's run at the cut is, and how long the run the edit's words carry there is. Both
 * are 0 when the two runs do not read the same.
 */
interface SpacingKeptAtLinkCut {
    pageSpacingLength: number;
    writtenSpacingLength: number;
}

/** No spacing kept at the cut: the edit beside the link starts at the link's edge and writes all its words. */
const NO_SPACING_KEPT_AT_LINK_CUT: SpacingKeptAtLinkCut = { pageSpacingLength: 0, writtenSpacingLength: 0 };

/** A word of digits alone, kept only as itself. */
const NUMBER_WORD = /^\p{N}+$/u;

/** The endings a kept content word may gain: a plural or a verb's third person ("Fees", "Boxes"). */
const INFLECTIONAL_SUFFIXES = ["s", "es"];

/** A lower-case word ending in a consonant and "y", whose plural makes the "y" "ies" ("party", "policy"). */
const CONSONANT_THEN_Y = /[b-df-hj-np-tv-xz]y$/u;

/** A word in Hangul, which writes its particles and its plural onto a noun with no space. */
const HANGUL_WORD = /^\p{Script=Hangul}+$/u;

/**
 * The particles and the plural marker Korean writes onto a noun with no space, longest
 * first where one begins another: "계약서" -> "계약서들" (plural), "계약서를" (object),
 * "계약서들을" (both).
 */
const KOREAN_NOUN_ENDINGS = ["으로", "에서", "에게", "까지", "부터", "보다", "처럼", "들", "은", "는", "이", "가", "을", "를", "의", "에", "와", "과", "도", "만", "로"];

/** The fewest syllables a bare Korean noun has for its endings to be read (`koreanNounWithEndings`). */
const KOREAN_NOUN_SYLLABLES_FOR_ENDINGS = 2;

/**
 * What each run of digits in a Hangul word reads as when two clause references are
 * compared (`sameHangulWordAroundNumbers`), so a changed number still holds its place:
 * "제1조" and "제12조" both read "제0조", and "제조" does not.
 */
const NUMBER_PLACEHOLDER = "0";

/**
 * Words too common to say the link survives: "the Fee Schedule" -> "the Annex B"
 * keeps "the" and replaces the link all the same.
 */
const WORDS_THAT_KEEP_NO_LINK = new Set(["the", "a", "an", "of", "and", "or", "to", "in", "for"]);

export class LinkFieldStriker {
    /**
     * Strikes every HYPERLINK of the paragraph the planned edits replace, and
     * refreshes the model when it struck any. Whether the edits replace a link is
     * the card's instruction for it (`LinkInstruction`, found by the link's display
     * text and address); a link the card gave none for is judged by its words.
     *
     * CASES, by the instruction for a link the edits reach
     *   - "keep"      -> never struck: the new words are written inside the link, at its address
     *   - "change"    -> struck whole; the new words are written as a new link to `newAddress`
     *                    (`linksReplacedByLinks`, `ParagraphSplicer`)
     *   - "needs_url" -> struck whole; the new words are written as plain text
     *   - none        -> struck whole when the edits leave no content word of it; then the new
     *                    words are plain text
     *
     * CASES, by how a struck link is spelled
     *   - a complex-field link                     -> its runs struck by `revision`, begin to end
     *   - a `w:fldSimple` link                     -> rewritten as a complex field, then struck the same way
     *   - a `w:hyperlink` element, its address     -> rewritten as the complex HYPERLINK field of its address
     *     in `hyperlinkTargets`                       (or its `w:anchor`), then struck the same way
     *   - a `w:hyperlink` element, its address     -> the runs inside it struck; the element stays around them
     *     not known
     *
     * EXAMPLE, accepted text "see the Fee Schedule now", link result 4..20 "the Fee Schedule", no instruction
     *   edits [{ 3..20 -> "" }]                          -> struck, range 4..20
     *   edits [{ 4..20 -> "Annex B" }]                   -> struck: "Annex B" keeps no word of the link
     *   edits [{ 8..20 -> "Annex B" }] (-> "the Annex B") -> struck: "the" is untouched but keeps no link
     *   edits [{ 8..11 -> "Fees" }] ("Fee" -> "Fees")    -> kept: "Schedule" is untouched
     *   link "Schedule" alone, edit -> "Schedules"       -> kept: "Schedule" + "s"
     *
     * EXAMPLE, the same paragraph with an instruction for the link
     *   edits [{ 4..20 -> "Annex B" }], "keep"                         -> kept: "Annex B" written inside the link
     *   edits [{ 8..11 -> "Fees" }], "change" to https://y.example/fee -> struck; "the Fees Schedule" written as a
     *                                                                     link to https://y.example/fee
     *   edits [{ 4..20 -> "Annex B" }], "needs_url"                    -> struck; "Annex B" written as plain text
     *
     * EMPTY
     *   `linkInstructions` is [] for a paragraph whose card names no link: every link is judged by its words.
     */
    static strikeLinksTheEditsReplace(
        paragraphModel: ParagraphModel,
        edits: readonly EditOverAcceptedText[],
        revision: Revision,
        hyperlinkTargets: HyperlinkTargetsByRelationshipId,
        linkInstructions: readonly LinkInstruction[],
    ): LinksStruckWhole {
        const deletingRanges = LinkFieldStriker.mergedDeletingRanges(edits);
        /**
         * WHEN no edit deletes a character and the card names no link
         *   CONSEQUENCE
         *     no link can lose its words, and none is struck by an instruction
         *
         * WHY an instruction keeps the check going
         *   An insertion inside a link's text changes its words: "change" and "needs_url"
         *   strike the link for it ("Fee Schedule" -> "Fee Payment Schedule").
         */
        if (deletingRanges.length === 0 && linkInstructions.length === 0) return NO_LINK_STRUCK;
        const paragraph = paragraphModel.paragraph;
        const hyperlinkElements = Array.from(paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "hyperlink"));
        const fieldSimples = Array.from(paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "fldSimple"));
        const holdsComplexFields = paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "fldChar").length > 0;
        /**
         * WHY this is checked before anything is read
         *   The striker runs for every redline that deletes a character; most
         *   paragraphs hold no link in any spelling, and reading their pieces cost
         *   the engine half again its time under the generated-load sweep.
         */
        if (hyperlinkElements.length === 0 && fieldSimples.length === 0 && !holdsComplexFields) return NO_LINK_STRUCK;
        const instructionByLinkOpeningElement = LinkFieldStriker.instructionsOfLinks(paragraphModel, hyperlinkTargets, linkInstructions);
        const acceptedRangesByRun = new Map<Element, AcceptedTextRange[]>();
        for (const piece of paragraphModel.acceptedPieces()) {
            const rangesOfRun = acceptedRangesByRun.get(piece.run);
            const pieceRange = { startOffset: piece.startOffset, endOffset: piece.endOffset };
            /**
             * CASES
             *   - the run has a piece already -> this one added to its list
             *   - its first piece             -> a list of one
             */
            if (rangesOfRun !== undefined) rangesOfRun.push(pieceRange);
            else acceptedRangesByRun.set(piece.run, [pieceRange]);
        }
        const overlapsAnyDeletion = (acceptedRange: AcceptedTextRange): boolean =>
            deletingRanges.some((deletingRange) => deletingRange.startOffset < acceptedRange.endOffset && deletingRange.endOffset > acceptedRange.startOffset);
        const displayTextTheEditsEmpty = (resultRuns: Element[]): StruckDisplayText[] | null => {
            const resultRanges = resultRuns.flatMap((resultRun) => acceptedRangesByRun.get(resultRun) ?? []);
            /** A link with no live display text, or one no deletion reaches, is the edits' to leave alone. */
            if (resultRanges.length === 0 || !resultRanges.some(overlapsAnyDeletion)) return null;
            const contentWords = resultRanges.flatMap((resultRange) => LinkFieldStriker.contentWordsAt(paragraphModel.text, resultRange));
            /** A content word no edit touches survives as it is: the link keeps its field. */
            if (contentWords.some((contentWordAtOffset) => !overlapsAnyDeletion(contentWordAtOffset))) return null;
            const textWrittenOverLink = edits
                .filter((edit) => resultRanges.some((resultRange) => edit.startOffset < resultRange.endOffset && edit.endOffset > resultRange.startOffset))
                .map((edit) => edit.replacementText)
                .join(" ");
            /** The edits write a touched content word back, or with an "s" or "es": they change the link's words. */
            if (LinkFieldStriker.writesAContentWordBack(textWrittenOverLink, contentWords)) return null;
            /**
             * Each struck range's display text: `startOffset` is its first accepted-text character,
             * `endOffset` the first one after it.
             *
             * EXAMPLE
             *   "see the Fee Schedule now", range 4..20 -> "the Fee Schedule"
             */
            return resultRanges.map((resultRange) => ({ ...resultRange, displayText: paragraphModel.text.slice(resultRange.startOffset, resultRange.endOffset), replacementLink: null }));
        };
        /**
         * The display text of a link an edit reaches, struck whole by a "change" or a
         * "needs_url": an edit that takes any of its characters, or inserts strictly
         * inside it (`ParagraphLinks.editReachesLinkText`), over the link's whole bounds.
         *
         * NULL
         *   The link holds no live text, or no edit reaches it: it stays as it is.
         */
        const displayTextAnEditReaches = (resultRuns: Element[], newAddress: string | null): StruckLink | null => {
            const resultRanges = resultRuns.flatMap((resultRun) => acceptedRangesByRun.get(resultRun) ?? []);
            const firstLiveResultRun = resultRuns.find((resultRun) => acceptedRangesByRun.has(resultRun));
            /** A link with no live display text is the edits' to leave alone. */
            if (firstLiveResultRun === undefined) return null;
            /**
             * The link's text from its first live character to its last, across the runs it is cut into.
             * EXAMPLE runs "Fee " 8..12 and "Schedule" 12..20 -> 8..20
             */
            const linkBounds = {
                startOffset: Math.min(...resultRanges.map((resultRange) => resultRange.startOffset)),
                endOffset: Math.max(...resultRanges.map((resultRange) => resultRange.endOffset)),
            };
            /** No edit reaches the link's text: it stays as it is. */
            if (!edits.some((edit) => ParagraphLinks.editReachesLinkText(edit, linkBounds))) return null;
            const replacementLink = LinkFieldStriker.replacementLinkTo(newAddress, firstLiveResultRun);
            return {
                linkBounds,
                replacementLink,
                /** Each range's display text, `startOffset` up to the first character after it: range 4..20 of "see the Fee Schedule now" -> "the Fee Schedule". */
                struckRanges: resultRanges.map((resultRange) => ({ ...resultRange, displayText: paragraphModel.text.slice(resultRange.startOffset, resultRange.endOffset), replacementLink })),
            };
        };
        /**
         * What the edits do to one link, by the card's instruction for it.
         *
         * CASES
         *   - "keep"      -> null: never struck, the new words go inside the link
         *   - "change"    -> struck once an edit reaches it, the new words a link to `newAddress`
         *   - "needs_url" -> struck once an edit reaches it, the new words plain text
         *   - none        -> struck when the edits leave no content word of it (`displayTextTheEditsEmpty`)
         *
         * NULL
         *   The link stays as it is.
         */
        const linkStruckFor = (resultRuns: Element[], linkOpeningElement: Element): StruckLink | null => {
            const instruction = instructionByLinkOpeningElement.get(linkOpeningElement);
            /** WHEN the card gave no instruction for the link CONSEQUENCE its words decide, as before instructions existed. */
            if (instruction === undefined) {
                const emptiedRanges = displayTextTheEditsEmpty(resultRuns);
                /** NULL: the edits keep a content word of the link's text. */
                if (emptiedRanges === null) return null;
                return { linkBounds: null, replacementLink: null, struckRanges: emptiedRanges };
            }
            switch (instruction.action) {
                case "keep":
                    return null;
                case "change":
                    return displayTextAnEditReaches(resultRuns, instruction.newAddress);
                case "needs_url":
                    return displayTextAnEditReaches(resultRuns, null);
            }
        };
        let rewroteAFieldSimple = false;
        for (const fieldSimple of fieldSimples) {
            /** A `w:fldSimple` Word redraws is the unlinker's. */
            if (fieldResultIsRedrawnByWord(fieldSimpleInstructionOf(fieldSimple))) continue;
            /** The edits leave it standing: it stays a simple field, edited in place. */
            if (linkStruckFor(Array.from(fieldSimple.getElementsByTagNameNS(WORDPROCESSINGML_NS, "r")), fieldSimple) === null) continue;
            const instruction = instructionByLinkOpeningElement.get(fieldSimple);
            const rewrittenBeginRun = LinkFieldStriker.rewriteAsComplexField(fieldSimple);
            /**
             * WHEN the card instructed the link and it was rewritten
             *   CONSEQUENCE
             *     the field now opens with its new begin run, by which the complex walk below finds the instruction
             */
            if (instruction !== undefined && rewrittenBeginRun !== null) instructionByLinkOpeningElement.set(rewrittenBeginRun, instruction);
            rewroteAFieldSimple = true;
        }
        let linkCount = 0;
        const struckRanges: StruckDisplayText[] = [];
        const linksReplacedByLinks: StruckLinkReplacedByLink[] = [];
        /**
         * Counts one link the edits strike and keeps its struck display text, and, when the
         * card writes the new words as a link, the bounds the engine reads that link's edits by.
         *
         * WHY one recorder
         *   A link is struck from a complex field or from a simple field rewritten as one;
         *   both are counted, and their ranges kept, the same way.
         */
        const recordStruck = (struckLink: StruckLink): void => {
            linkCount += 1;
            struckRanges.push(...struckLink.struckRanges);
            /** WHEN the new words are written as a link CONSEQUENCE the engine reads which edits write them by the link's bounds. */
            if (struckLink.replacementLink !== null && struckLink.linkBounds !== null) linksReplacedByLinks.push({ ...struckLink.linkBounds, replacementLink: struckLink.replacementLink });
        };
        /**
         * Every complete complex field of the paragraph, a simple-field link rewritten above
         * among them (`complexFieldWalkOf`); a paragraph with none gives the empty walk.
         */
        const complexFields = LinkFieldStriker.completeComplexFieldsOf(paragraph);
        for (const field of complexFields) {
            /** A field Word redraws is the unlinker's, struck by the plan as ordinary text once unlinked. */
            if (fieldResultIsRedrawnByWord(field.fieldInstruction)) continue;
            /** `runsBeginToEnd[0]` is the field's `fldChar begin` run: the element that opens the link. */
            const struckLink = linkStruckFor(field.resultRuns, field.runsBeginToEnd[0]);
            /** The edits leave the link standing: it keeps its field. */
            if (struckLink === null) continue;
            LinkFieldStriker.strikeOrRemoveRuns(field.runsBeginToEnd, paragraph, revision);
            recordStruck(struckLink);
        }
        for (const hyperlinkElement of hyperlinkElements) {
            const linkRuns = Array.from(hyperlinkElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "r"));
            /** A link whose runs a field struck above already went with that field. */
            if (linkRuns.every((linkRun) => LinkFieldStriker.sitsInsideDeletion(linkRun, paragraph))) continue;
            const struckLink = linkStruckFor(linkRuns, hyperlinkElement);
            /** The edits leave the link standing: the element keeps its words and its address. */
            if (struckLink === null) continue;
            const fieldInstruction = LinkFieldStriker.hyperlinkFieldInstructionOf(hyperlinkElement, hyperlinkTargets);
            /**
             * CASES
             *   - the element's address is known -> the element becomes its HYPERLINK field, and the
             *                                        field's runs, codes and result, are struck
             *   - it is not                      -> the runs inside the element are struck, as Word
             *                                        leaves them when the address cannot be written
             */
            const runsToStrike = fieldInstruction === null ? linkRuns : LinkFieldStriker.rewriteHyperlinkElementAsComplexField(hyperlinkElement, fieldInstruction);
            LinkFieldStriker.strikeOrRemoveRuns(runsToStrike, paragraph, revision);
            recordStruck(struckLink);
        }
        if (linkCount > 0) paragraphModel.refresh();
        return { linkCount, struckRanges, linksReplacedByLinks };
    }

    /**
     * The new link the words of one planned edit are written as: the link of the
     * struck link whose text the edit reaches (`ParagraphLinks.editReachesLinkText`),
     * read in the offsets the edits were planned in, before they are moved past the strike.
     *
     * NULL
     *   The edit reaches no link struck for a "change": its words are plain text.
     *
     * EXAMPLE, link "the Fee Schedule" 4..20 struck for a "change" to https://y.example/fee
     *   edit 8..20 -> "Annex B" -> the link to https://y.example/fee
     *   edit 21..24 -> "today"  -> null: it sits after the link
     */
    static replacementLinkReachedBy(plannedEdit: EditOverAcceptedText, linksStruckWhole: LinksStruckWhole): ReplacementLinkOfStruckLink | null {
        const reachedLink = linksStruckWhole.linksReplacedByLinks.find((linkReplacedByLink) => ParagraphLinks.editReachesLinkText(plannedEdit, linkReplacedByLink));
        /** NULL: no link the edit reaches is written anew. */
        if (reachedLink === undefined) return null;
        return reachedLink.replacementLink;
    }

    /**
     * The runs of a new HYPERLINK field to `address` around `resultRun`, in order:
     * begin, instruction, separate, the result run, end. The splicer writes them all
     * inside one insertion when a "change" writes its new words as a link, so Accept
     * keeps the link and Reject takes it away with its words.
     *
     * EXAMPLE
     *   "https://y.example/annex", a run "the Annex B"
     *   -> [begin] [instrText ' HYPERLINK "https://y.example/annex" '] [separate] <w:r>the Annex B</w:r> [end]
     */
    static hyperlinkFieldRunsAround(resultRun: Element, address: string): Element[] {
        const markerRuns = LinkFieldStriker.complexFieldMarkerRuns(resultRun.ownerDocument as Document, ` HYPERLINK ${LinkFieldStriker.fieldArgument(address)} `);
        return [markerRuns.beginRun, markerRuns.instructionRun, markerRuns.separateRun, resultRun, markerRuns.endRun];
    }

    /**
     * The card's instruction for each link of the paragraph, by the element that opens
     * the link: the instruction that names the link's display text and its address as
     * the reader read them (`ParagraphLinks.readingOf`), so the engine finds the link
     * the web's apply recorded it for wherever the paragraph sits now.
     *
     * EMPTY
     *   No instruction, or none that names a link of the paragraph as it reads now (the
     *   user rewrote the link's words since the card was made): every link is judged by
     *   its words.
     *
     * EDGE CASE
     *   Two links of one paragraph with the same words and the same address both take the
     *   first instruction that names them.
     */
    private static instructionsOfLinks(paragraphModel: ParagraphModel, hyperlinkTargets: HyperlinkTargetsByRelationshipId, linkInstructions: readonly LinkInstruction[]): Map<Element, LinkInstruction> {
        const instructionByLinkOpeningElement = new Map<Element, LinkInstruction>();
        /** EMPTY: the card names no link. */
        if (linkInstructions.length === 0) return instructionByLinkOpeningElement;
        for (const link of ParagraphLinks.withLiveTextOf(paragraphModel)) {
            /** EXAMPLE link 4..20 of "see the Fee Schedule now" -> "the Fee Schedule" */
            const displayText = paragraphModel.text.slice(link.startOffset, link.endOffset);
            const address = ParagraphLinks.addressOf(link, hyperlinkTargets);
            const instruction = linkInstructions.find((linkInstruction) => linkInstruction.linkText === displayText && linkInstruction.linkAddress === address);
            /** WHEN an instruction names the link CONSEQUENCE it decides the link. */
            if (instruction !== undefined) instructionByLinkOpeningElement.set(link.linkOpeningElement, instruction);
        }
        return instructionByLinkOpeningElement;
    }

    /**
     * The link a struck link's new words are written as.
     *
     * NULL
     *   Plain text: there is no new address ("needs_url").
     */
    private static replacementLinkTo(newAddress: string | null, formattingSourceRun: Element): ReplacementLinkOfStruckLink | null {
        /** NULL: no new address ("needs_url"), so the new words are written as plain text. */
        if (newAddress === null) return null;
        return { address: newAddress, formattingSourceRun };
    }

    /**
     * The planned edits split at every link edge they cross through punctuation, so
     * an edit of a link's words stays within the link and a change of the
     * punctuation beside it is its own edit outside.
     *
     * WHY
     *   The planner edits a word together with the punctuation that ends it:
     *   "Schedule." -> "Schedules." is one edit over 4..13 when the link's text is
     *   "Schedule" at 4..12 and the "." follows it outside. Spliced as planned, the
     *   new word was written after the field and the link was left empty, or held
     *   only "Fee " of "Fee Schedule". Split at the link's end, the words go inside
     *   and the punctuation outside; punctuation written back unchanged needs no
     *   edit at all and stays where it is.
     *
     * CASES, per edit and link it overlaps
     *   - its replacement writes the text beside the link back as it was   -> split at that edge,
     *                                                                         the text beside the
     *                                                                         link left as it is
     *   - it runs past the link's end through text with no letter or digit -> split at the link's end
     *   - it starts before the link through such text                      -> split at the link's start
     *   - the text beside the link holds a word, and the replacement's
     *     words for the link write one of its words back                  -> split there too
     *                                                                       (`writesTheLinksWordBack`)
     *   - the text beside the link holds a word, and they do not           -> left as planned: it
     *                                                                       replaces more than the link
     *
     * EXAMPLE, "see the Schedule." with the link "Schedule" at 8..16
     *   edit 8..17 "Schedule." -> "Schedules."   -> 8..16 "Schedules"; the "." is written back, no edit
     *   edit 8..17 "Schedule." -> "Schedules!"   -> 8..16 "Schedules" and 16..17 "!"
     *   edit 8..17 "Schedule." -> "Annex."       -> 8..16 "Annex" (the striker then judges it)
     *
     * An edit that runs across two links with only punctuation and spacing between them
     * is first cut at that stretch (`editSplitBetweenLinks`), so each link is edited on
     * its own.
     */
    static editsKeptWithinLinks<Edit extends EditOverAcceptedText>(paragraphModel: ParagraphModel, plannedEdits: readonly Edit[]): Edit[] {
        const linkRanges = LinkFieldStriker.linkDisplayRangesOf(paragraphModel);
        /** A paragraph with no link has nothing to keep edits within. */
        if (linkRanges.length === 0) return [...plannedEdits];
        const paragraphText = paragraphModel.text;
        const linkRangesInOrder = [...linkRanges].sort((left, right) => left.startOffset - right.startOffset);
        const editsJoinedAcrossStretches = LinkFieldStriker.editsJoinedAcrossLinkStretches(plannedEdits, linkRangesInOrder, paragraphText);
        return editsJoinedAcrossStretches.flatMap((plannedEdit) => LinkFieldStriker.editSplitBetweenLinks(plannedEdit, linkRangesInOrder, paragraphText)).flatMap((plannedEdit) => {
            let editOverLink: Edit = { ...plannedEdit };
            const editsBesideLinks: Edit[] = [];
            for (const linkRange of linkRanges) {
                /**
                 * WHEN the edit runs from inside the link past its end through text with no letter or digit
                 *   CONSEQUENCE
                 *     split at the link's end: the link's word count of the replacement stays inside,
                 *     the rest is written over the punctuation after it, or nothing when unchanged
                 *
                 * EXAMPLE, "see the Schedule." link 8..16, edit 8..17 "Schedule." -> "Schedules!"
                 *   after link "." (16..17); inside "Schedule" holds 1 word
                 *   replacement split after its 1st word: inside 8..16 "Schedules", after 16..17 "!"
                 */
                const cutAtGluedEnd = editOverLink.startOffset < linkRange.endOffset && editOverLink.endOffset > linkRange.endOffset ? LinkFieldStriker.cutAtGluedLinkEnd(paragraphText, editOverLink, linkRange.endOffset) : null;
                if (cutAtGluedEnd !== null) {
                    /**
                     * WHEN the link's end cuts one of the edit's words, and the replacement's
                     *      matching word can be cut the same way (`cutAtGluedLinkEnd`)
                     *   CONSEQUENCE
                     *     split there: the link takes the replacement up to the cut, and the text
                     *     glued after the link takes the rest, or nothing when written back as it was
                     *
                     * EXAMPLE, "[부록 1]을 참조" link 0..4, edit 3..5 "1을" -> "2를"
                     *   the link's end at 4 cuts "1을" into "1" | "을"; "2를" is cut after its digits at 1
                     *   -> 3..4 "2" inside, 4..5 "를" after
                     */
                    const textAfterLink = paragraphText.slice(linkRange.endOffset, editOverLink.endOffset);
                    const writtenAfterLink = editOverLink.replacementText.slice(cutAtGluedEnd);
                    /** Text glued after the link written back as it was needs no edit of its own. */
                    if (writtenAfterLink !== textAfterLink) editsBesideLinks.push({ ...plannedEdit, startOffset: linkRange.endOffset, endOffset: editOverLink.endOffset, replacementText: writtenAfterLink });
                    editOverLink = { ...editOverLink, endOffset: linkRange.endOffset, replacementText: editOverLink.replacementText.slice(0, cutAtGluedEnd) };
                } else if (editOverLink.startOffset < linkRange.endOffset && editOverLink.endOffset > linkRange.endOffset && editOverLink.replacementText.endsWith(paragraphText.slice(linkRange.endOffset, editOverLink.endOffset))) {
                    /**
                     * WHEN the replacement ends with the text after the link, as it was
                     *   CONSEQUENCE
                     *     cut there: the link takes the rest of the replacement, and the text after
                     *     it, written back unchanged, needs no edit
                     *
                     * WHY before the word count below
                     *   An edit of the link's last word that runs on over words left as they
                     *   are, "[Fee] schedule" -> "Annex schedule", would otherwise be left whole
                     *   and strike "schedule" only to write it again.
                     *
                     * EXAMPLE, "see [Fee] schedule" link 4..7, edit 4..16 "Fee schedule" -> "Annex schedule"
                     *   text after the link " schedule" (7..16), the replacement ends with it
                     *   -> 4..7 "Annex": 14 - 9 = 5 characters of the replacement kept inside
                     */
                    const textAfterLinkLength = editOverLink.endOffset - linkRange.endOffset;
                    editOverLink = { ...editOverLink, endOffset: linkRange.endOffset, replacementText: editOverLink.replacementText.slice(0, editOverLink.replacementText.length - textAfterLinkLength) };
                } else if (editOverLink.startOffset < linkRange.endOffset && editOverLink.endOffset > linkRange.endOffset) {
                    const textAfterLink = paragraphText.slice(linkRange.endOffset, editOverLink.endOffset);
                    const insideRange = { startOffset: editOverLink.startOffset, endOffset: linkRange.endOffset };
                    const insideWordCount = LinkFieldStriker.wordRunsOf(paragraphText.slice(insideRange.startOffset, insideRange.endOffset)).length;
                    /** The replacement's first words, as many as the edit covers inside the link. */
                    const writtenInsideLink = editOverLink.replacementText.slice(0, LinkFieldStriker.offsetAfterWords(editOverLink.replacementText, insideWordCount));
                    if (!WordTokenizer.holdsALetterOrDigit(textAfterLink) || LinkFieldStriker.writesTheLinksWordBack(paragraphText, insideRange, insideWordCount, writtenInsideLink)) {
                        const splitOffset = LinkFieldStriker.offsetAfterWords(editOverLink.replacementText, insideWordCount);
                        const writtenAfterLink = editOverLink.replacementText.slice(splitOffset);
                        /**
                         * The spacing that opens both the text after the link and the words written after
                         * it, which stays as the page reads it when the two read the same (`spacingKeptAfterLink`).
                         *
                         * EXAMPLE, "the [Party] agrees to" link 4..9, edit 4..16 "Party agrees" -> "Parties agree"
                         *   after the link " agrees" (9..16), written after it " agree": page run 1, written run 1
                         *   -> the edit after the link is `9 + 1` = 10..16, "agree" from written offset 1: the space at 9 kept plain
                         */
                        const keptSpacing = LinkFieldStriker.spacingKeptAfterLink(textAfterLink, writtenAfterLink);
                        /** Punctuation written back as it was needs no edit of its own. */
                        if (writtenAfterLink !== textAfterLink) editsBesideLinks.push({ ...plannedEdit, startOffset: linkRange.endOffset + keptSpacing.pageSpacingLength, endOffset: editOverLink.endOffset, replacementText: writtenAfterLink.slice(keptSpacing.writtenSpacingLength) });
                        editOverLink = { ...editOverLink, endOffset: linkRange.endOffset, replacementText: editOverLink.replacementText.slice(0, splitOffset) };
                    }
                }
                /**
                 * WHEN the edit runs from before the link, through text with no letter or digit, into it
                 *   CONSEQUENCE
                 *     split at the link's start: the replacement's last words, as many as the edit
                 *     covers inside the link, stay inside; what comes before them goes before it
                 *
                 * EXAMPLE, "see (Schedule)" link 5..13, edit 4..13 "(Schedule" -> "[Schedules"
                 *   before link "(" (4..5); inside "Schedule" holds 1 word
                 *   replacement split before its last word: before 4..5 "[", inside 5..13 "Schedules"
                 */
                if (editOverLink.endOffset > linkRange.startOffset && editOverLink.startOffset < linkRange.startOffset && editOverLink.replacementText.startsWith(paragraphText.slice(editOverLink.startOffset, linkRange.startOffset))) {
                    /**
                     * WHEN the replacement starts with the text before the link, as it was
                     *   CONSEQUENCE
                     *     cut there: the link takes the rest of the replacement, and the text before
                     *     it, written back unchanged, needs no edit
                     *
                     * EXAMPLE, "제[1조]에" link 1..3, edit 0..3 "제1조" -> "제2조"
                     *   text before the link "제" (0..1), the replacement starts with it
                     *   -> 1..3 "2조": the replacement from character 1 on
                     */
                    const textBeforeLinkLength = linkRange.startOffset - editOverLink.startOffset;
                    editOverLink = { ...editOverLink, startOffset: linkRange.startOffset, replacementText: editOverLink.replacementText.slice(textBeforeLinkLength) };
                } else if (editOverLink.endOffset > linkRange.startOffset && editOverLink.startOffset < linkRange.startOffset) {
                    const textBeforeLink = paragraphText.slice(editOverLink.startOffset, linkRange.startOffset);
                    const insideRange = { startOffset: linkRange.startOffset, endOffset: editOverLink.endOffset };
                    const insideWordCount = LinkFieldStriker.wordRunsOf(paragraphText.slice(insideRange.startOffset, insideRange.endOffset)).length;
                    /** The replacement's last words, as many as the edit covers inside the link. */
                    const writtenInsideLink = editOverLink.replacementText.slice(LinkFieldStriker.offsetBeforeLastWords(editOverLink.replacementText, insideWordCount));
                    if (!WordTokenizer.holdsALetterOrDigit(textBeforeLink) || LinkFieldStriker.writesTheLinksWordBack(paragraphText, insideRange, insideWordCount, writtenInsideLink)) {
                        const splitOffset = LinkFieldStriker.offsetBeforeLastWords(editOverLink.replacementText, insideWordCount);
                        const writtenBeforeLink = editOverLink.replacementText.slice(0, splitOffset);
                        /**
                         * The spacing that closes both the text before the link and the words written
                         * before it, which stays as the page reads it when the two read the same (`spacingKeptBeforeLink`).
                         *
                         * EXAMPLE, "the Party [Fee] is" link 10..13, edit 4..13 "Party Fee" -> "Parties Fees"
                         *   before the link "Party " (4..10), written before it "Parties ": page run 1, written run 1
                         *   -> the edit before the link is 4..`10 - 1` = 4..9, "Parties" up to written offset `8 - 1` = 7: the space at 9 kept plain
                         */
                        const keptSpacing = LinkFieldStriker.spacingKeptBeforeLink(textBeforeLink, writtenBeforeLink);
                        /** Punctuation written back as it was needs no edit of its own. */
                        if (writtenBeforeLink !== textBeforeLink) editsBesideLinks.push({ ...plannedEdit, startOffset: editOverLink.startOffset, endOffset: linkRange.startOffset - keptSpacing.pageSpacingLength, replacementText: writtenBeforeLink.slice(0, writtenBeforeLink.length - keptSpacing.writtenSpacingLength) });
                        editOverLink = { ...editOverLink, startOffset: linkRange.startOffset, replacementText: editOverLink.replacementText.slice(splitOffset) };
                    }
                }
            }
            /** WHEN a cut left the edit over the link writing the link's own text back CONSEQUENCE that edit writes nothing and goes. */
            const editsWritingSomething = editOverLink.replacementText === paragraphText.slice(editOverLink.startOffset, editOverLink.endOffset) ? editsBesideLinks : [...editsBesideLinks, editOverLink];
            return editsWritingSomething.sort((left, right) => left.startOffset - right.startOffset);
        });
    }

    /**
     * The planned edits with each group the planner's alignment carried across the
     * stretch between two links joined into one edit, so `editSplitBetweenLinks` deals
     * its words to the links by the kept-word match.
     *
     * WHY
     *   The planner aligns words, not links: "see [Fee Schedule], [Schedule B] now" ->
     *   "see Fee Schedules, Schedule C now" matched the first link's "Schedule" to the
     *   request's second "Schedule" and planned "Schedules, " inserted inside the first
     *   link, the "," between the links struck, and "Schedule B" -> "C". No edit ran
     *   across the stretch, so none was cut, and once accepted the first link read "Fee
     *   Schedules, Schedule" and "C" stood after the emptied second (R17c review r17i,
     *   2026-10-16). Joined, the group is one edit over "Schedule, Schedule B" ->
     *   "Schedules, Schedule C", cut where its words belong.
     *
     * CASES, per pair of links next to each other
     *   - words stand between the two links                                 -> left as planned
     *   - fewer than two edits reach from inside the first link to inside
     *     the second                                                        -> left as planned: a lone
     *                                                                          edit is cut on its own
     *   - none of them strikes the stretch's punctuation writing no word    -> left as planned: each
     *                                                                          link's words stayed on
     *                                                                          its side
     *   - otherwise                                                         -> joined, from the first
     *                                                                          one's start to the last
     *                                                                          one's end, the text
     *                                                                          between them written back
     *
     * EXAMPLE, "see Fee Schedule, Schedule B now", links 4..16 and 18..28
     *   planned 8..8 "" -> "Schedules, "   16..17 "," -> ""   18..28 "Schedule B" -> "C"
     *   joined  8..28 -> "Schedules, " + "Schedule" (8..16) + "" + " " (17..18) + "C" = "Schedules, Schedule C"
     */
    private static editsJoinedAcrossLinkStretches<Edit extends EditOverAcceptedText>(plannedEdits: readonly Edit[], linkRangesInOrder: readonly AcceptedTextRange[], paragraphText: string): Edit[] {
        let editsInOrder = [...plannedEdits].sort((left, right) => left.startOffset - right.startOffset);
        /** Each link and the one after it: `linkIndex + 1` is in range while `linkIndex + 1 < length`. */
        for (let linkIndex = 0; linkIndex + 1 < linkRangesInOrder.length; linkIndex += 1) {
            const linkBefore = linkRangesInOrder[linkIndex];
            const linkAfter = linkRangesInOrder[linkIndex + 1];
            /** WHEN words stand between the two links CONSEQUENCE their edits are each link's own: no join. */
            if (WordTokenizer.holdsALetterOrDigit(paragraphText.slice(linkBefore.endOffset, linkAfter.startOffset))) continue;
            /**
             * The edits over the two links and the stretch between them, an insertion counted
             * when it falls strictly inside: one at the first link's start goes in front of it,
             * one at the second's end after it.
             */
            const reachesThePair = (plannedEdit: Edit): boolean => plannedEdit.startOffset < linkAfter.endOffset && plannedEdit.endOffset > linkBefore.startOffset;
            const firstEditIndex = editsInOrder.findIndex(reachesThePair);
            /**
             * The last edit that reaches the pair, walked from the end: -1 when none does.
             *
             * WHY a loop and not `findLastIndex`
             *   The add-in compiles this package for es6, whose lib has no `findLastIndex`
             *   (es2023): its tsc failed on the call (TS2550, -23, 2026-10-16).
             *
             * EXAMPLE
             *   edits 8..8, 16..17, 18..28, 40..44 over links 4..16 and 18..28 -> 2, the edit 18..28
             */
            let lastEditIndex = editsInOrder.length - 1;
            while (lastEditIndex >= 0 && !reachesThePair(editsInOrder[lastEditIndex])) lastEditIndex -= 1;
            /** WHEN fewer than two edits reach the pair CONSEQUENCE nothing to join. */
            if (firstEditIndex === -1 || lastEditIndex <= firstEditIndex) continue;
            const editsOverPair = editsInOrder.slice(firstEditIndex, lastEditIndex + 1);
            const reachesIntoFirstLink = editsOverPair[0].startOffset < linkBefore.endOffset;
            const reachesIntoSecondLink = editsOverPair[editsOverPair.length - 1].endOffset > linkAfter.startOffset;
            const strikesStretchWritingNoWord = editsOverPair.some(
                (plannedEdit) => plannedEdit.startOffset < linkAfter.startOffset && plannedEdit.endOffset > linkBefore.endOffset && !WordTokenizer.holdsALetterOrDigit(plannedEdit.replacementText),
            );
            /**
             * WHEN the edits do not run from inside the first link to inside the second, or none
             * strikes the stretch's punctuation writing no word in its place
             *   CONSEQUENCE
             *     the planner's alignment kept each link's words on its side: left as planned
             */
            if (!reachesIntoFirstLink || !reachesIntoSecondLink || !strikesStretchWritingNoWord) continue;
            /**
             * The text the group writes from its first start to its last end.
             *
             * CASES, per edit of the group
             *   - an edit with another after it -> its replacement, then the source text it left
             *                                      standing up to that next edit's start;
             *                                      `editIndexInGroup + 1` is the next edit while
             *                                      `editIndexInGroup + 1 < length`
             *   - the last edit                 -> its replacement alone
             */
            const joinedReplacementText = editsOverPair
                .map((plannedEdit, editIndexInGroup) =>
                    editIndexInGroup + 1 < editsOverPair.length ? plannedEdit.replacementText + paragraphText.slice(plannedEdit.endOffset, editsOverPair[editIndexInGroup + 1].startOffset) : plannedEdit.replacementText,
                )
                .join("");
            const joinedEdit: Edit = { ...editsOverPair[0], endOffset: editsOverPair[editsOverPair.length - 1].endOffset, replacementText: joinedReplacementText };
            editsInOrder = [...editsInOrder.slice(0, firstEditIndex), joinedEdit, ...editsInOrder.slice(lastEditIndex + 1)];
        }
        return editsInOrder;
    }

    /**
     * Where to cut an edit's replacement when the link's end cuts one of the edit's words
     * in two, a word with text glued after the link: the part inside the link and the part
     * glued after it, matched in the replacement's word at the same place.
     *
     * WHY
     *   Korean writes its particles onto a word with no space, so the diff reads the link's
     *   last word and the particle after it as one word: "[부록 1]을" -> "부록 2를" is one
     *   edit of "1을" into "2를". Counted as one word, the replacement could not be cut, and
     *   the number landed outside the link or the particle inside it, or the link was
     *   struck (R17c gap 1, -80's cases, 2026-10-16).
     *
     * CASES, the replacement's word at the cut word's place, in this order
     *   - it ends with the glued part, as it was     -> cut before it ("2을", "계약서들를", "합의서를")
     *   - it starts with the link's part, as it was  -> cut after it ("계약서들을", "계약서의")
     *   - the link's part is a number, and the word
     *     starts with digits then other characters  -> cut after the digits ("2를")
     *   - none of these                               -> null: no cut found
     *
     * EXAMPLE, the link's part and glued part, the replacement's word, the cut in that word
     *   "1"      | "을"   "2을"        -> 1: "2" | "을"
     *   "계약서" | "를"   "계약서들를" -> 4: "계약서들" | "를"
     *   "계약서" | "를"   "계약서의"   -> 3: "계약서" | "의"
     *   "1"      | "을"   "2를"        -> 1: "2" | "를"
     *
     * RETURNS
     *   The offset in the replacement to cut at.
     *
     * NULL
     *   The link's end falls between two of the edit's words, the replacement has no word at
     *   that place, or no case above finds a cut.
     */
    private static cutAtGluedLinkEnd(paragraphText: string, edit: EditOverAcceptedText, linkEndOffset: number): number | null {
        const editRuns = LinkFieldStriker.wordRunsOf(paragraphText.slice(edit.startOffset, edit.endOffset));
        /**
         * EXAMPLE
         *   link end 12, edit start 11 -> 12 - 11 = 1: the link's end one character into the edit's text
         */
        const linkEndInEdit = linkEndOffset - edit.startOffset;
        const cutRunIndex = editRuns.findIndex((editRun) => editRun.startOffset < linkEndInEdit && editRun.endOffset > linkEndInEdit);
        /** WHEN the link's end falls between two of the edit's words CONSEQUENCE no word glued to the link: no cut here. */
        if (cutRunIndex < 0) return null;
        const cutRun = editRuns[cutRunIndex];
        /**
         * EXAMPLE, the edit's word "1을" at 0..2, the link's end 1 character in
         *   link's part "1"   (0..1 - 0)    glued part "을" (1 - 0..)
         */
        const linkPart = cutRun.text.slice(0, linkEndInEdit - cutRun.startOffset);
        const gluedPart = cutRun.text.slice(linkEndInEdit - cutRun.startOffset);
        const replacementRun = LinkFieldStriker.wordRunsOf(edit.replacementText)[cutRunIndex];
        /** WHEN the replacement has fewer words than the edit's word's place CONSEQUENCE no matching word to cut. */
        if (replacementRun === undefined) return null;
        const cutInRun = LinkFieldStriker.cutInGluedWord(replacementRun.text, linkPart, gluedPart);
        return cutInRun === null ? null : replacementRun.startOffset + cutInRun;
    }

    /**
     * Where one replacement word is cut to match a word the link's end cuts into
     * `linkPart` and `gluedPart`; the cases of `cutAtGluedLinkEnd`.
     *
     * NULL
     *   No case finds a cut.
     */
    private static cutInGluedWord(replacementWord: string, linkPart: string, gluedPart: string): number | null {
        /**
         * EXAMPLE
         *   "계약서들를" ends with "를": 5 - 1 = 4 -> "계약서들" | "를"
         */
        if (replacementWord.length > gluedPart.length && replacementWord.endsWith(gluedPart)) return replacementWord.length - gluedPart.length;
        if (replacementWord.length > linkPart.length && replacementWord.startsWith(linkPart)) return linkPart.length;
        const leadingDigits = /^\p{N}+(?=\P{N})/u.exec(replacementWord);
        /** WHEN the link's part is a number and the replacement's word leads with digits CONSEQUENCE cut after the digits: "2를" -> "2" | "를". */
        if (NUMBER_WORD.test(linkPart) && leadingDigits !== null) return leadingDigits[0].length;
        return null;
    }

    /**
     * The edit cut at every stretch between two links it runs across where that stretch
     * holds no letter or digit: the replacement's words are cut where each link's words are
     * written back on its own side (`wordCutBetweenLinks`), and the stretch takes what the
     * replacement writes between the two sides, words it adds there included. With no
     * link word written back, the side before takes as many words as the edit covers
     * there and the side after as many from the replacement's end as the edit covers
     * from the second link on.
     *
     * WHY
     *   Split at link edges one link at a time, an edit over "Schedule, Terms" found the
     *   other link's words beside each edge and was left whole: written as planned, the
     *   first link took "Schedules, Term" and the second was emptied, and in the complex
     *   spelling the first kept only "Fee " (R17c follow-up 1, -48, 2026-10-16).
     *
     * CASES, per pair of links next to each other
     *   - the edit does not run from inside the first to inside the second -> no cut
     *   - the stretch between them holds a letter or digit                  -> no cut: the
     *                                                                         edit is left whole
     *   - the edit covers no word before the stretch                        -> no cut: no word
     *                                                                         to deal out
     *   - a word of the replacement writes back only the second link's      -> the side after
     *     word                                                                starts there
     *   - none does                                                         -> the words dealt
     *                                                                         by count
     *   - the replacement has a word for after the stretch                  -> the stretch takes
     *                                                                         what it writes
     *                                                                         between the two
     *   - it has none                                                       -> the stretch takes
     *                                                                         nothing, and what
     *                                                                         is left goes after
     *
     * WHY the side after the stretch takes its words from the replacement's end
     *   Dealt every word after the first side's, the second link took a word the
     *   replacement adds between the two: "[Fee Schedule], [Terms]" -> "Fee Schedules, the
     *   Term" read "[Fee Schedules], [the Term]", and "[Fee][Terms]" -> "Fee and Terms" read
     *   "[Fee] [and Terms]" (R17c review, -17, 2026-10-16). Counted from the end, the second
     *   link takes as many words as it holds, and the words between go over the stretch.
     *
     * WHY the replacement's words are matched to the links before they are counted
     *   See `wordCutBetweenLinks`: counted alone, "[Fee Schedule], [Terms]" -> "Fees, Term"
     *   read "[Fees, Term]" (R17c review r17i, -17, 2026-10-16).
     *
     * EXAMPLE, "see Fee Schedule, Terms now", links 4..16 and 18..23, edit 4..23 -> "Fees, Term"
     *   the edit covers 2 words before the stretch; cut 1 writes back "fee" before and "terms" after
     *   -> min(2, 1) = 1 word before: 4..16 "Fees"; ", " written back, no edit; 18..23 "Term"
     *
     * EXAMPLE, "see Fee Schedule, Terms now", links 4..16 and 18..23, edit 8..23 "Schedule, Terms" -> "Schedules, Term"
     *   words before the stretch: "Schedule" (1); after it: "Terms" (1); the replacement holds 2
     *   -> 8..16 "Schedules"; the stretch 16..18 ", " is written back, so no edit; 18..23 "Term"
     *
     * EXAMPLE, the same edit -> "Schedules, the Term": the replacement holds 3
     *   word 0 "Schedules" before; the last 1, word `3 - 1` = 2 "Term", after; word 1 "the" between
     *   -> 8..16 "Schedules"; 16..18 ", " -> ", the "; 18..23 "Term"
     *
     * Each piece left over is then kept within its link by `editsKeptWithinLinks`.
     */
    private static editSplitBetweenLinks<Edit extends EditOverAcceptedText>(plannedEdit: Edit, linkRangesInOrder: readonly AcceptedTextRange[], paragraphText: string): Edit[] {
        const editPieces: Edit[] = [];
        let editLeft: Edit = { ...plannedEdit };
        /** Each link and the one after it: `linkIndex + 1` is in range while `linkIndex + 1 < length`. */
        for (let linkIndex = 0; linkIndex + 1 < linkRangesInOrder.length; linkIndex += 1) {
            const linkBefore = linkRangesInOrder[linkIndex];
            const linkAfter = linkRangesInOrder[linkIndex + 1];
            const stretchBetweenLinks = paragraphText.slice(linkBefore.endOffset, linkAfter.startOffset);
            const runsAcrossTheStretch = editLeft.startOffset < linkBefore.endOffset && editLeft.endOffset > linkAfter.startOffset;
            /** WHEN the edit does not run from inside one link into the next, or words stand between them CONSEQUENCE no cut here: the edit is left whole for this pair. */
            if (!runsAcrossTheStretch || WordTokenizer.holdsALetterOrDigit(stretchBetweenLinks)) continue;
            const wordsBeforeStretch = LinkFieldStriker.wordRunsOf(paragraphText.slice(editLeft.startOffset, linkBefore.endOffset)).length;
            /** WHEN the edit covers no word before the stretch (it starts on the link's last punctuation) CONSEQUENCE no word to deal out, so no cut. */
            if (wordsBeforeStretch === 0) continue;
            const replacementText = editLeft.replacementText;
            const replacementWords = LinkFieldStriker.wordRunsOf(replacementText);
            const wordsFromSecondLinkOn = LinkFieldStriker.wordRunsOf(paragraphText.slice(linkAfter.startOffset, editLeft.endOffset)).length;
            /**
             * The parts of the two links the edit covers: the first from where the edit starts,
             * or the link's start when the edit starts before it, to the link's end; the second
             * from its start to its end, or the edit's end when the edit stops inside it.
             */
            const firstLinkPartCovered: AcceptedTextRange = { startOffset: Math.max(editLeft.startOffset, linkBefore.startOffset), endOffset: linkBefore.endOffset };
            const secondLinkPartCovered: AcceptedTextRange = { startOffset: linkAfter.startOffset, endOffset: Math.min(linkAfter.endOffset, editLeft.endOffset) };
            /**
             * The index of the replacement's first word for the side after the stretch, dealt by
             * count: as many words from its end as the edit covers from the second link on, never
             * one dealt before.
             *
             * EXAMPLE
             *   replacement "Schedules, the Term" (3 words), 1 word after the stretch -> `3 - 1` = 2, "Term"
             *   replacement "Schedules" (1 word), 1 before and 1 after               -> max(1, `1 - 1` = 0) = 1: none left
             */
            const countedFirstWordIndexAfterStretch = Math.max(wordsBeforeStretch, replacementWords.length - wordsFromSecondLinkOn);
            const { wordsForSideBeforeStretch, firstWordIndexAfterStretch } = LinkFieldStriker.wordCutBetweenLinks(
                replacementWords,
                LinkFieldStriker.contentWordsAt(paragraphText, firstLinkPartCovered),
                LinkFieldStriker.contentWordsAt(paragraphText, secondLinkPartCovered),
                wordsBeforeStretch,
                countedFirstWordIndexAfterStretch,
            );
            /**
             * CASES
             *   - the side before the stretch takes no word (the best cut is before the
             *     replacement's first word)                                     -> 0: the first link's part is emptied
             *   - it takes some                                                 -> just past the last of them
             */
            const endOfWordsBeforeStretch = wordsForSideBeforeStretch === 0 ? 0 : LinkFieldStriker.offsetAfterWords(replacementText, wordsForSideBeforeStretch);
            /**
             * CASES
             *   - the replacement holds a word at that index -> where it starts
             *   - it holds none                              -> where the words before the stretch end
             */
            const startOfWordsAfterStretch = replacementWords.length > firstWordIndexAfterStretch ? replacementWords[firstWordIndexAfterStretch].startOffset : endOfWordsBeforeStretch;
            const writtenOverStretch = replacementText.slice(endOfWordsBeforeStretch, startOfWordsAfterStretch);
            editPieces.push({ ...editLeft, endOffset: linkBefore.endOffset, replacementText: replacementText.slice(0, endOfWordsBeforeStretch) });
            /** The stretch written back as it was needs no edit of its own. */
            if (writtenOverStretch !== stretchBetweenLinks) editPieces.push({ ...editLeft, startOffset: linkBefore.endOffset, endOffset: linkAfter.startOffset, replacementText: writtenOverStretch });
            editLeft = { ...editLeft, startOffset: linkAfter.startOffset, replacementText: replacementText.slice(startOfWordsAfterStretch) };
        }
        return [...editPieces, editLeft];
    }

    /**
     * Where the replacement's words are cut between two links: how many go to the first
     * link's side, and the index of the first word for the second's, the words between
     * written over the stretch. The cut is the one that writes back the most of each
     * link's covered words on its own side (`writesAContentWordBack`), counted per link
     * word; among cuts that tie, the count's own cut when it is one of them, else the
     * widest stretch between the first and last.
     *
     * WHY matched before counted
     *   Counted, a replacement with fewer words than the edit covers gave them all to the
     *   first link: "[Fee Schedule], [Terms]" -> "Fees, Term" read "[Fees, Term]" and the
     *   Terms link was gone; and a word added before a later link moved the cut, so
     *   "A [Fee Schedule], [Terms], [Policy]." -> "A Fee Schedules, the Term, the Policies."
     *   struck the Terms link (R17c review r17i, -17, 2026-10-16).
     *
     * WHY each link's words are scored on both sides, not the second link's first match
     *   A word both links hold matched the second link at the first word that held it:
     *   "[Fee Schedule], [Schedule B]" -> "Schedules, Schedule C" cut before "Schedules"
     *   or, with shared words passed over, fell back to the count, and the first link read
     *   "Schedules, Schedule". Scored, "Schedules" before the cut writes back the first
     *   link's "schedule" and "Schedule" after it the second's: cut 1 writes back 2, any
     *   other 1.
     *
     * WHY the count settles a tie
     *   "[Schedule A], [Schedule B]" -> "Schedules D, Schedules E" ties at cuts 1, 2 and 3:
     *   either "Schedules" writes back each link's "schedule" ("a" is no content word).
     *   Cut from the first tie to the last, the links read "[Schedules] D, [Schedules E]";
     *   the count's cut, 2, keeps both words of each link with it.
     *
     * CASES
     *   - no cut writes back any link word             -> the count's: as many words before as the edit
     *                                                     covers there, the side after from `countedFirstWordIndexAfterStretch`
     *   - the count's cut is among those that write
     *     back the most                                -> the count's, the side before capped at it
     *   - it is not                                    -> the side before up to the first such cut, the
     *                                                     side after from the last; the words between go
     *                                                     over the stretch
     *
     * EXAMPLE, scores per cut over the replacement's words
     *   "Fees, Term"           first "fee" "schedule", second "terms"    cut 0: 0+1  1: 1+1  2: 1+0       -> 1 before, after from 1
     *   "Schedules, the Term"  first "schedule", second "terms", count 2 cut 1: 1+1  2: 1+1  3: 1+0       -> 1 before, after from 2
     *   "Schedules, Schedule C" first "fee" "schedule", second
     *                           "schedule" "b", count 2                cut 0: 0+1  1: 1+1  2: 1+0  3: 1+0 -> 1 before, after from 1
     *   "Schedules D, Schedules E" first "schedule", second "schedule"
     *                           "b", count 2                           cut 0: 0+1  1: 1+1  2: 1+1  3: 1+1  4: 1+0 -> the count's: 2 and 2
     */
    private static wordCutBetweenLinks(
        replacementWords: readonly WordToken[],
        firstLinkContentWords: ContentWordAtOffset[],
        secondLinkContentWords: ContentWordAtOffset[],
        wordsBeforeStretch: number,
        countedFirstWordIndexAfterStretch: number,
    ): WordCutBetweenLinks {
        const linkWordsWrittenBackBy = (wordsOfOneSide: readonly WordToken[], linkContentWords: ContentWordAtOffset[]): number =>
            linkContentWords.filter((linkContentWord) => wordsOfOneSide.some((replacementWord) => LinkFieldStriker.writesAContentWordBack(replacementWord.text, [linkContentWord]))).length;
        /**
         * The score of every cut, indexed by the count of words before it: from 0, all words
         * after it, to `replacementWords.length`, all before; `replacementWords.length + 1`
         * cuts in all.
         */
        const linkWordsWrittenBackPerCut = Array.from({ length: replacementWords.length + 1 }, (_unused, wordsBeforeCut) =>
            linkWordsWrittenBackBy(replacementWords.slice(0, wordsBeforeCut), firstLinkContentWords) + linkWordsWrittenBackBy(replacementWords.slice(wordsBeforeCut), secondLinkContentWords),
        );
        const mostLinkWordsWrittenBack = Math.max(...linkWordsWrittenBackPerCut);
        const cutsWritingMostBack = linkWordsWrittenBackPerCut.flatMap((linkWordsWrittenBack, wordsBeforeCut) => (linkWordsWrittenBack === mostLinkWordsWrittenBack ? [wordsBeforeCut] : []));
        /** WHEN no cut writes back a word of either link CONSEQUENCE the words dealt by count, as before matching. */
        if (mostLinkWordsWrittenBack === 0) return { wordsForSideBeforeStretch: wordsBeforeStretch, firstWordIndexAfterStretch: countedFirstWordIndexAfterStretch };
        /** WHEN the count's cut writes back as many link words as any CONSEQUENCE it settles the tie. */
        if (cutsWritingMostBack.includes(countedFirstWordIndexAfterStretch)) {
            return { wordsForSideBeforeStretch: Math.min(wordsBeforeStretch, countedFirstWordIndexAfterStretch), firstWordIndexAfterStretch: countedFirstWordIndexAfterStretch };
        }
        /** The first and last cuts that write back the most; `length - 1` is the last one's index. */
        const firstCutWritingMostBack = cutsWritingMostBack[0];
        const lastCutWritingMostBack = cutsWritingMostBack[cutsWritingMostBack.length - 1];
        return { wordsForSideBeforeStretch: Math.min(wordsBeforeStretch, firstCutWritingMostBack), firstWordIndexAfterStretch: lastCutWritingMostBack };
    }

    /**
     * The spacing that opens both the page's text after a link and the words an edit cut
     * at the link's end writes there, which the edit after the link leaves as the page
     * reads it, when the two runs read the same.
     *
     * WHY
     *   The planner writes two changed words as one replacement, the space between them
     *   inside it ("Party agrees" -> "Parties agree"). Cut at the link's end, the edit
     *   after the link opened with that space on both sides, so the space was struck and
     *   written again: `{end}[- agrees][+ agree]` (§14, -26 at 431ae93ba, 2026-10-19).
     *
     * WHY the tokenizer's predicate (`WordTokenizer.whitespaceReadsTheSame`)
     *   Two runs of spacing between the same words are the same spacing exactly when the
     *   tokenizer reads them so, the rule every reading of the model's spacing follows:
     *   a no-break space, a tab or two spaces retyped as one space stay the page's whole
     *   run, and a line break the card writes as a space is the card's change, written
     *   as the card writes it. Compared character for character, a no-break space was
     *   struck and written as a space (-38, 2026-10-19); kept as the shorter run, a
     *   replaced line break stayed and two spaces lost one (-38, 405394f69).
     *
     * WHY not the half planned on its own (`TextEditPlanner.editsBetween`)
     *   The half is part of one words edit, not a request: the plan writes no spacing-only
     *   change, so "see [Fee] now" -> "see now" left "see  now", and a deletion at the
     *   half's edge kept the space the whole edit took: "see Schedules ." (-a5, 2026-10-19).
     *
     * EXAMPLE, <NBSP> a no-break space, <BR> a line break
     *   page " agrees",        written " agree"   -> page 1, written 1
     *   page "<NBSP>agrees",   written " agree"   -> page 1, written 1: the no-break space stays
     *   page "  agrees",       written " agree"   -> page 2, written 1: both spaces stay
     *   page "<BR>agrees",     written " agree"   -> none: the card replaces the break
     *   page " ",              written ""         -> none: the edit takes the space with its words
     */
    private static spacingKeptAfterLink(pageText: string, writtenText: string): SpacingKeptAtLinkCut {
        const pageSpacingLength = WordTokenizer.whitespaceRunLengthStartingAt(pageText, 0);
        const writtenSpacingLength = WordTokenizer.whitespaceRunLengthStartingAt(writtenText, 0);
        /**
         * WHEN the page's run and the written run read otherwise, or the page has none CONSEQUENCE none kept.
         *
         * EXAMPLE, the runs opening each text: `slice(0, length)`
         *   page "  agrees", run 2 -> slice(0, 2) "  "; written " agree", run 1 -> slice(0, 1) " "   -> read the same
         *   page "\nagrees", run 1 -> slice(0, 1) "\n"; written " agree", run 1 -> slice(0, 1) " "   -> a break and a space differ
         */
        if (pageSpacingLength === 0 || !WordTokenizer.whitespaceReadsTheSame(pageText.slice(0, pageSpacingLength), writtenText.slice(0, writtenSpacingLength))) return NO_SPACING_KEPT_AT_LINK_CUT;
        return { pageSpacingLength, writtenSpacingLength };
    }

    /**
     * The spacing that closes both the page's text before a link and the words an edit cut
     * at the link's start writes there, the mirror of `spacingKeptAfterLink`.
     *
     * EXAMPLE, <NBSP> a no-break space, <BR> a line break
     *   page "Party ",        written "Parties "  -> page 1, written 1
     *   page "Party<NBSP>",   written "Parties "  -> page 1, written 1: the no-break space stays
     *   page "Party<BR>",     written "Parties "  -> none: the card replaces the break
     *   page "Party",         written "Parties "  -> none: the page closes with no spacing
     *
     * The runs end at each text's end: "Party " length 6, run 1 -> slice(`6 - 1` = 5) " ".
     */
    private static spacingKeptBeforeLink(pageText: string, writtenText: string): SpacingKeptAtLinkCut {
        const pageSpacingLength = WordTokenizer.whitespaceRunLengthEndingAt(pageText, pageText.length);
        const writtenSpacingLength = WordTokenizer.whitespaceRunLengthEndingAt(writtenText, writtenText.length);
        /**
         * WHEN the page's run and the written run read otherwise, or the page has none CONSEQUENCE none kept.
         *
         * EXAMPLE, the runs closing each text: `slice(length - run)`
         *   page "Party ", length 6, run 1 -> slice(`6 - 1` = 5) " "; written "Parties ", length 8, run 1 -> slice(7) " " -> read the same
         *   page "Party\n", length 6, run 1 -> slice(5) "\n";        written "Parties ", slice(7) " "                -> a break and a space differ
         */
        if (pageSpacingLength === 0 || !WordTokenizer.whitespaceReadsTheSame(pageText.slice(pageText.length - pageSpacingLength), writtenText.slice(writtenText.length - writtenSpacingLength))) return NO_SPACING_KEPT_AT_LINK_CUT;
        return { pageSpacingLength, writtenSpacingLength };
    }

    /**
     * Whether the replacement's words for the part of a link an edit covers write one of
     * that part's words back (`writesAContentWordBack`), so the edit is split at the
     * link's edge though the text beside the link holds words.
     *
     * WHY
     *   "see [Schedule] and [Annex]." -> "see Schedules." is one edit over "Schedule and
     *   Annex". The text after the first link holds words, so the edit was left whole, and
     *   a complex field took "Schedules" after its end run: the link was empty after
     *   Accept (R17c follow-up 2, -48, 2026-10-16). The replacement's first word,
     *   "Schedules", writes the link's "Schedule" back, so it is the link's edited word,
     *   and the rest of the replacement ("." for " and Annex.") is written beside it.
     *
     * CASES
     *   - the edit covers no word inside the link -> false: no word to hand back
     *   - the words written there write one back  -> true
     *   - they write none back                    -> false: the edit replaces the link, and
     *                                                the striker judges it
     *
     * EXAMPLE
     *   link part "Schedule", written "Schedules" -> true
     *   link part "Schedule", written "Annex"     -> false
     */
    private static writesTheLinksWordBack(paragraphText: string, insideRange: AcceptedTextRange, insideWordCount: number, writtenInsideLink: string): boolean {
        /** WHEN the edit covers none of the link's words, only its edge's punctuation CONSEQUENCE no word to hand back: false. */
        if (insideWordCount === 0) return false;
        return LinkFieldStriker.writesAContentWordBack(writtenInsideLink, LinkFieldStriker.contentWordsAt(paragraphText, insideRange));
    }

    /**
     * The words of a text as the striker compares them, in order, each with its offsets
     * in the text: the tokenizer's runs of letters and digits inside each of the redline
     * diff's units, each character read with the marks the fold composes into it
     * (`WordTokenizer.contentWordRuns`), the one cut the striker and the tokenizer share.
     *
     * EXAMPLE
     *   "詳細は別紙1。"            -> 詳細 0..2, は 2..3, 別紙 3..5, 1 5..6
     *   "Fee's Schedule,"         -> Fee 0..3, s 4..5, Schedule 6..14
     *   "Re\u{301}sume\u{301}"    -> one word 0..8, as the composed "Résumé" is
     */
    private static wordRunsOf(text: string): WordToken[] {
        return WordTokenizer.contentWordRuns(text);
    }

    /**
     * Where a replacement splits after its first `wordCount` words: just past the last
     * letter of that word. With fewer words than that, just past its last letter, so
     * only its trailing punctuation is left after.
     *
     * EXAMPLE
     *   "Schedules!",       1 -> 9:  "Schedules" | "!"
     *   "Schedules. Also",  1 -> 9:  "Schedules" | ". Also"
     *   "Fee Schedules,",   2 -> 13: "Fee Schedules" | ","
     *   "!",                1 -> 0:  "" | "!"
     */
    private static offsetAfterWords(replacementText: string, wordCount: number): number {
        const wordRuns = LinkFieldStriker.wordRunsOf(replacementText);
        /**
         * CASES
         *   - it holds at least that many words -> past the `wordCount`-th; `wordCount - 1` is its index
         *   - fewer                             -> past its last word, or 0 when it holds none
         */
        const lastWordInside = wordRuns.length >= wordCount && wordCount > 0 ? wordRuns[wordCount - 1] : wordRuns[wordRuns.length - 1];
        return lastWordInside === undefined ? 0 : lastWordInside.endOffset;
    }

    /**
     * Where a replacement splits before its last `wordCount` words: at the first letter
     * of the first of them. With fewer words than that, at its first letter, so only
     * its leading punctuation is left before.
     *
     * EXAMPLE
     *   "[Schedules", 1 -> 1: "[" | "Schedules"
     *   "Schedules",  1 -> 0: "" | "Schedules"
     *   "(",          1 -> 1: "(" | ""
     */
    private static offsetBeforeLastWords(replacementText: string, wordCount: number): number {
        const wordRuns = LinkFieldStriker.wordRunsOf(replacementText);
        /**
         * CASES
         *   - it holds at least that many words -> at the first of its last `wordCount`; `length - wordCount` is its index
         *   - fewer                             -> at its first word, or its end when it holds none
         */
        const firstWordInside = wordRuns.length >= wordCount && wordCount > 0 ? wordRuns[wordRuns.length - wordCount] : wordRuns[0];
        return firstWordInside === undefined ? replacementText.length : firstWordInside.startOffset;
    }

    /**
     * The accepted-text bounds of every link of the paragraph, in each spelling:
     * a complex HYPERLINK field's result, a `w:fldSimple` HYPERLINK's runs and a
     * `w:hyperlink` element's runs, each from its first live character to its last.
     *
     * EXAMPLE
     *   "see the Fee Schedule now", link "the Fee Schedule"  -> [4..20]
     *   a link whose every run is struck                       -> left out: it holds no live text
     */
    private static linkDisplayRangesOf(paragraphModel: ParagraphModel): AcceptedTextRange[] {
        return ParagraphLinks.withLiveTextOf(paragraphModel).map(({ startOffset, endOffset }) => ({ startOffset, endOffset }));
    }

    /**
     * Where each link of the paragraph starts, and the element that opens it, so a
     * pure insertion written at a link's start goes in front of the link, not into it
     * (`RedlineEngine`, `ParagraphSplicer.insertBefore`).
     *
     * WHY
     *   The splicer puts a pure insertion in front of the run its offset starts, in that
     *   run's own container. At a link's end the next run is outside the link, so "Terms
     *   below" was written outside; at its start the run is the link's first, so "see
     *   [Terms] now" -> "see the Terms now" was written "see [the Terms] now", and in a
     *   pair ", [Terms]" -> " and Terms" gave "[and Terms]" (R17c review gap 3, -17,
     *   2026-10-16). Written in front of the element that opens the link, both edges
     *   leave the new words outside it.
     *
     * CASES, the element that opens a link, by its spelling
     *   - a complex HYPERLINK field -> its `fldChar begin` run
     *   - a `w:fldSimple` HYPERLINK  -> the `w:fldSimple` element
     *   - a `w:hyperlink` element    -> the `w:hyperlink` element
     *
     * EXAMPLE
     *   "see Terms now", the link's result "Terms" at 4..9 -> [{ startOffset: 4, linkOpeningElement: <begin run> }]
     */
    static linkStartsOf(paragraphModel: ParagraphModel): LinkStart[] {
        return ParagraphLinks.withLiveTextOf(paragraphModel).map(({ startOffset, linkOpeningElement }) => ({ startOffset, linkOpeningElement }));
    }

    /**
     * The content words of one stretch of display text, as the tokenizer's content key
     * reads a word (`WordTokenizer.contentKey`: lower case, under the fold), with where
     * each stands: every word but those of `WORDS_THAT_KEEP_NO_LINK`.
     *
     * EXAMPLE
     *   text "see the Fee Schedule now", range 4..20
     *   "the" at 4..7 left out; "fee" at `4 + 4` = 8..11; "schedule" at `4 + 8` = 12..20
     */
    private static contentWordsAt(paragraphText: string, displayRange: AcceptedTextRange): ContentWordAtOffset[] {
        const displayText = paragraphText.slice(displayRange.startOffset, displayRange.endOffset);
        return LinkFieldStriker.wordRunsOf(displayText)
            .map((wordRun) => ({ contentWord: WordTokenizer.contentKey(wordRun.text), startOffset: displayRange.startOffset + wordRun.startOffset, endOffset: displayRange.startOffset + wordRun.endOffset }))
            .filter((wordAtOffset) => !WORDS_THAT_KEEP_NO_LINK.has(wordAtOffset.contentWord));
    }

    /**
     * The plural of a lower-case word that ends in a consonant and "y": the "y" made
     * "ies". `slice(0, -1)` drops the final "y".
     *
     * WHY
     *   The kept-word rule knew only "s" and "es", so "[Party]" -> "Parties" and
     *   "[Policy]" -> "Policies", everyday contract edits, wrote none of the link's words
     *   back and the link was struck (R17c review gap 2, -17, 2026-10-16).
     *
     * EXAMPLE
     *   "party"  -> "parties"     "policy" -> "policies"
     *   "key"    -> null: a vowel before the "y" takes a plain "s" ("keys")
     *   "fee"    -> null
     *
     * NULL
     *   the word does not end in a consonant and "y".
     */
    private static pluralOfConsonantY(lowerCaseWord: string): string | null {
        return CONSONANT_THEN_Y.test(lowerCaseWord) ? `${lowerCaseWord.slice(0, -1)}ies` : null;
    }

    /**
     * Whether a Korean word is a noun with one or more of the particles and the plural
     * Korean writes onto it (`KOREAN_NOUN_ENDINGS`), so a link's noun written back with
     * them is the link's word, as "Fees" is "Fee".
     *
     * WHY
     *   The kept-word rule knew only English endings, so "[계약서]를" -> "계약서들를"
     *   wrote none of the link's words back and the link was struck (R17c gap 1,
     *   2026-10-16). The endings are a closed list rather than any longer word, as "Term"
     *   is no "Termination": "계약" -> "계약서" is another word.
     *
     * WHY a noun of one syllable is left out
     *   Many endings are also the last syllable of another noun: "부" -> "부도" (a
     *   department, a default), "회의" -> "회" (a meeting, a round), "사" -> "사과". Read as
     *   the bare noun with an ending, each kept the link over a different noun (-eb's
     *   cases, 2026-10-16); every such case has a one-syllable bare noun. Left out, a
     *   one-syllable noun with a real ending ("조" -> "조의") strikes its link instead.
     *
     * EXAMPLE
     *   "계약서", "계약서들"   -> true:  "들"
     *   "계약서", "계약서들을" -> true:  "들" then "을"
     *   "회사",   "회사도"     -> true:  "도", the bare noun of two syllables
     *   "부",     "부도"       -> false: a one-syllable bare noun
     *   "계약",   "계약서"     -> false: "서" is no ending
     *   "fee",    "fees"       -> false: not Hangul
     */
    private static koreanNounWithEndings(noun: string, writtenWord: string): boolean {
        /** WHEN either word is not Hangul, or the written word does not start with the noun CONSEQUENCE no Korean ending to read. */
        if (!HANGUL_WORD.test(noun) || !HANGUL_WORD.test(writtenWord) || !writtenWord.startsWith(noun)) return false;
        /**
         * WHEN the bare noun is one syllable, each Hangul syllable one character
         *   CONSEQUENCE
         *     no ending is read: see WHY above. "부" is 1 < 2, "회사" is 2
         */
        if ([...noun].length < KOREAN_NOUN_SYLLABLES_FOR_ENDINGS) return false;
        return LinkFieldStriker.isRunOfKoreanEndings(writtenWord.slice(noun.length));
    }

    /**
     * Whether a Hangul word that holds digits is written back as the same word around its
     * numbers, whatever they now are: compared with each run of digits as one
     * placeholder, the same shape, or that shape with Korean endings written on or taken
     * off.
     *
     * WHY
     *   Korean writes a clause reference's number into the word with no space, "제1조",
     *   where English writes "Annex 2024" as two words. Compared whole, "1조" -> "2조"
     *   wrote none of the link's words back and the link was struck in every spelling of
     *   the reference (R17c gap 1, -eb, 2026-10-16). Its letters around the number play
     *   the part "Annex" plays in "[Annex 2024]" -> "Annex 2025", which keeps its link.
     *
     * WHY a placeholder, not the letters alone
     *   With its digits deleted, "제1조" read as "제조", so "[제1조]" -> "제조" (Article 1,
     *   then manufacturing) kept the link over another word (-eb's review, 2026-10-16). The
     *   placeholder keeps the number's place: "제0조" is not "제조", nor "제조0".
     *
     * CASES
     *   - the word holds no digit, only digits, or letters other than Hangul -> false
     *   - the written word is a number alone                                  -> false: kept only as itself
     *   - the written word has the same shape                                 -> true
     *   - the same shape with Korean endings written on, or taken off         -> true
     *   - another shape                                                       -> false: another word
     *
     * EXAMPLE, the shapes compared
     *   "1조"   "2조"     -> "0조" "0조"       true
     *   "제1조" "제2조"   -> "제0조" "제0조"   true
     *   "제1조" "제1조의" -> "제0조" "제0조의" true:  "의" written on
     *   "제1조" "제조"    -> "제0조" "제조"    false: the number is gone
     *   "제1조" "제조1"   -> "제0조" "제조0"   false: the number moved
     *   "1조"   "3항"     -> "0조" "0항"       false
     *   "30도"  "40"      -> false: a number alone, though "0" is "0도" with "도" taken off
     *   "1"     "2"       -> false: a number alone is kept only as itself
     */
    private static sameHangulWordAroundNumbers(contentWord: string, writtenWord: string): boolean {
        const contentLetters = contentWord.replace(/\p{N}+/gu, "");
        /** WHEN the word holds no digit, or nothing but digits, or its letters are not Hangul CONSEQUENCE no clause reference to read. */
        if (contentLetters.length === contentWord.length || contentLetters.length === 0 || !HANGUL_WORD.test(contentLetters)) return false;
        /**
         * WHEN the written word is a number alone: "[30도]" -> "40", "[1만]" -> "2"
         * CONSEQUENCE not written back. Its shape "0" is "0도" with the ending "도" taken off,
         *   but a number is kept only as itself (-eb's review of 7af9f798f, 2026-10-16).
         */
        if (NUMBER_WORD.test(writtenWord)) return false;
        const shapeOf = (word: string): string => word.replace(/\p{N}+/gu, NUMBER_PLACEHOLDER);
        const contentShape = shapeOf(contentWord);
        const writtenShape = shapeOf(writtenWord);
        if (writtenShape === contentShape) return true;
        /**
         * CASES
         *   - the written shape is the word's with endings written on: "제0조" -> "제0조의"
         *   - the word's shape is the written one's with endings: "제0조의" -> "제0조"
         */
        return LinkFieldStriker.endingsWrittenOn(contentShape, writtenShape) || LinkFieldStriker.endingsWrittenOn(writtenShape, contentShape);
    }

    /**
     * Whether `longerShape` is `shorterShape` with Korean noun endings and nothing else
     * after it, for words whose numbers stand as placeholders: a reference's number
     * makes it no bare one-syllable noun, so no syllable count applies.
     *
     * EXAMPLE
     *   "제0조", "제0조의" -> true
     *   "제0조", "제0항"   -> false
     */
    private static endingsWrittenOn(shorterShape: string, longerShape: string): boolean {
        return longerShape.startsWith(shorterShape) && LinkFieldStriker.isRunOfKoreanEndings(longerShape.slice(shorterShape.length));
    }

    /**
     * Whether a text is one or more Korean noun endings and nothing else.
     *
     * EXAMPLE
     *   "들을" -> true:  "들", then "을"
     *   ""     -> false: no ending
     *   "서"   -> false
     */
    private static isRunOfKoreanEndings(text: string): boolean {
        /** WHEN nothing is left after the noun CONSEQUENCE no ending was written: the words are the same, which the caller tests first. */
        if (text.length === 0) return false;
        return KOREAN_NOUN_ENDINGS.some((ending) => text.startsWith(ending) && (text.length === ending.length || LinkFieldStriker.isRunOfKoreanEndings(text.slice(ending.length))));
    }

    /**
     * Whether the text the edits write over a link writes one of its content words
     * back: the word itself, or the word with an inflectional "s" or "es" added, or
     * its "y" made "ies", letters compared without case. A number is written back only
     * as itself.
     *
     * EXAMPLE
     *   written "Fees",            link "the Fee Schedule" -> true:  "fee" + "s"
     *   written "Schedules",       link "Schedule"         -> true
     *   written "Fee's",           link "Fee"              -> true:  tokenized "fee", "s"
     *   written "Annex B",         link "the Fee Schedule" -> false: "the" is no content word
     *   written "Feedback Form",   link "Fee Schedule"     -> false: "feedback" is another word
     *   written "Termination",     link "Term"             -> false
     *   written "Annex 10",        link "Section 1"        -> false: a number is kept only as itself
     *   written "",                link "the Fee Schedule" -> false: the link's text is deleted
     */
    private static writesAContentWordBack(textWrittenOverLink: string, contentWords: ContentWordAtOffset[]): boolean {
        const writtenWords = LinkFieldStriker.wordRunsOf(textWrittenOverLink).map((writtenWord) => WordTokenizer.contentKey(writtenWord.text));
        const writtenAs = (contentWord: string, writtenWord: string): boolean => {
            /**
             * CASES
             *   - the same word                          -> kept
             *   - a number                               -> kept only as itself: "1" is not "10"
             *   - a word, with "s" or "es" added          -> kept: "Fee" -> "Fees", "Box" -> "Boxes"
             *   - a word, with "s" or "es" taken off       -> kept: "Services" -> "Service", "Boxes" -> "Box"
             *   - a word ending in a consonant and "y",
             *     with the "y" made "ies", or back         -> kept: "Party" -> "Parties", "Policies" -> "Policy"
             *   - a Korean noun, with particles or the
             *     plural written on, or taken off          -> kept: "계약서" -> "계약서들", "계약서를" -> "계약서"
             *   - a Hangul word holding digits, written
             *     back around its number's place, with
             *     endings or without                       -> kept: "1조" -> "2조", "제1조" -> "제1조의";
             *                                                 struck: "제1조" -> "제조", "제조1"
             */
            if (writtenWord === contentWord) return true;
            if (NUMBER_WORD.test(contentWord)) return false;
            if (LinkFieldStriker.sameHangulWordAroundNumbers(contentWord, writtenWord)) return true;
            if (LinkFieldStriker.pluralOfConsonantY(contentWord) === writtenWord || LinkFieldStriker.pluralOfConsonantY(writtenWord) === contentWord) return true;
            if (LinkFieldStriker.koreanNounWithEndings(contentWord, writtenWord) || LinkFieldStriker.koreanNounWithEndings(writtenWord, contentWord)) return true;
            return INFLECTIONAL_SUFFIXES.some((inflectionalSuffix) => writtenWord === contentWord + inflectionalSuffix || contentWord === writtenWord + inflectionalSuffix);
        };
        return contentWords.some((contentWordAtOffset) => writtenWords.some((writtenWord) => writtenAs(contentWordAtOffset.contentWord, writtenWord)));
    }

    /**
     * The struck display text no planned edit deletes, as edits that write it back
     * over itself: once the edits are moved past the strike they become insertions
     * of those characters where the link stood, so the request still reads.
     *
     * EXAMPLE
     *   struck 4..20 "the Fee Schedule", edits [8..20 -> "Annex B"]
     *   4..8 is deleted by no edit -> [{ 4..8 -> "the " }]
     *   struck 4..20, edits [3..20 -> ""]  -> []: every struck character was deleted
     *   struck 4..20, edits [12..12 -> "Payment "] -> [{ 4..12 -> "the Fee " }, { 12..20 -> "Schedule" }]:
     *     cut where the insertion goes, so it lands between them, "the Fee Payment Schedule"
     *
     * WHY an insertion cuts the written-back text
     *   A link struck for an insertion inside it ("change", "needs_url") keeps every
     *   character; written back whole, the stretch would merge in front of the
     *   insertion and the new words would land after "Schedule".
     *
     * WHY each written-back stretch carries its struck link's `replacementLink`
     *   Struck for a "change", the link's kept words are part of the new link: "the "
     *   of "the Fee Schedule" -> "the Annex B" is written inside it with "Annex B".
     */
    static struckTextTheEditsKeep(struckRanges: readonly StruckDisplayText[], plannedEdits: readonly EditOverAcceptedText[]): EditWritingWordsOrLink[] {
        /**
         * Where the written-back text is cut: each deletion, and each insertion as an empty
         * range at its offset, in offset order.
         * EXAMPLE edits [8..11 -> "Fees", 12..12 -> "Payment "] -> [8..11, 12..12]
         */
        const cutRanges = [
            ...LinkFieldStriker.mergedDeletingRanges(plannedEdits),
            ...plannedEdits.filter((plannedEdit) => plannedEdit.startOffset === plannedEdit.endOffset).map((insertion) => ({ startOffset: insertion.startOffset, endOffset: insertion.startOffset })),
        ].sort((left, right) => left.startOffset - right.startOffset || left.endOffset - right.endOffset);
        const editsWritingStruckTextBack: EditWritingWordsOrLink[] = [];
        for (const struckRange of struckRanges) {
            let uncoveredStartOffset = struckRange.startOffset;
            const writeBackUpTo = (uncoveredEndOffset: number): void => {
                /**
                 * EXAMPLE, struck 4..20 "the Fee Schedule", uncovered 4..8
                 *   `4 - 4`, `8 - 4` -> displayText.slice(0, 4) = "the "
                 *
                 * CASES
                 *   - characters lie between the last cut and this one -> written back
                 *   - two cuts meet, or one starts the stretch          -> nothing to write back
                 */
                if (uncoveredEndOffset > uncoveredStartOffset) {
                    editsWritingStruckTextBack.push({
                        startOffset: uncoveredStartOffset,
                        endOffset: uncoveredEndOffset,
                        replacementText: struckRange.displayText.slice(uncoveredStartOffset - struckRange.startOffset, uncoveredEndOffset - struckRange.startOffset),
                        replacementLink: struckRange.replacementLink,
                    });
                }
            };
            for (const cutRange of cutRanges) {
                /**
                 * A cut wholly before or after this struck stretch, or an insertion at either of its
                 * edges, leaves it whole: an insertion at offset 4 of 4..20 has `endOffset` 4 <= 4.
                 */
                if (cutRange.endOffset <= struckRange.startOffset || cutRange.startOffset >= struckRange.endOffset) continue;
                writeBackUpTo(Math.min(cutRange.startOffset, struckRange.endOffset));
                uncoveredStartOffset = Math.max(uncoveredStartOffset, cutRange.endOffset);
            }
            writeBackUpTo(struckRange.endOffset);
        }
        return editsWritingStruckTextBack;
    }

    /**
     * Edits in offset order with every two that overlap, or that meet where one of
     * them is an insertion, made one: from the first's start to the later end,
     * writing the first's text then the second's. Two replacements that only meet
     * stay apart, as a split at a link's edge leaves them, and so do two edits whose
     * words are written as different links, or one as a link and one as plain text.
     *
     * EXAMPLE
     *   [4..4 -> "the ", 4..4 -> "Annex B"]       -> [4..4 -> "the Annex B"]
     *   [3..4 -> "", 4..4 -> "X"]                 -> [3..4 -> "X"]
     *   [8..16 -> "Schedules", 16..17 -> "!"]     -> unchanged: two replacements that meet
     *   [4..4 -> "the " (link), 4..4 -> "Annex B" (link)] -> [4..4 -> "the Annex B" (link)]: one new link
     *   [3..4 -> "", 4..4 -> "Annex B" (link)]    -> unchanged: the deletion is no part of the link
     *
     * WHY edits of different links never overlap here
     *   The planned edits do not overlap one another, and the struck text written back
     *   fills only what no edit deletes, so two edits overlap only within one struck link.
     */
    static mergedTouchingEdits(editsInOffsetOrder: readonly EditWritingWordsOrLink[]): EditWritingWordsOrLink[] {
        const mergedEdits: EditWritingWordsOrLink[] = [];
        for (const edit of editsInOffsetOrder) {
            const previousEdit = mergedEdits[mergedEdits.length - 1];
            const overlapsPrevious = previousEdit !== undefined && edit.startOffset < previousEdit.endOffset;
            const meetsPreviousAsInsertion = previousEdit !== undefined && edit.startOffset === previousEdit.endOffset
                && (edit.startOffset === edit.endOffset || previousEdit.startOffset === previousEdit.endOffset);
            const writesTheSameLinkAsPrevious = previousEdit !== undefined && previousEdit.replacementLink === edit.replacementLink;
            /**
             * CASES
             *   - it overlaps the previous edit, or meets it with either an insertion,
             *     and both write plain words or the same link                          -> one edit, writing both texts in order
             *   - it starts after a gap, meets a replacement as a replacement, or
             *     writes another link than the previous edit                           -> an edit of its own
             */
            if (previousEdit !== undefined && writesTheSameLinkAsPrevious && (overlapsPrevious || meetsPreviousAsInsertion)) {
                previousEdit.endOffset = Math.max(previousEdit.endOffset, edit.endOffset);
                previousEdit.replacementText += edit.replacementText;
            } else mergedEdits.push({ ...edit });
        }
        return mergedEdits;
    }

    /**
     * The edits that delete characters, merged where they touch or overlap, in
     * offset order; an insertion (`startOffset === endOffset`) takes no character.
     *
     * EXAMPLE
     *   edits 3..4, 4..20, 25..25   -> 3..20 (the insertion at 25 takes nothing)
     */
    private static mergedDeletingRanges(edits: readonly AcceptedTextRange[]): AcceptedTextRange[] {
        const deletingRanges = edits.filter((edit) => edit.endOffset > edit.startOffset).map((edit) => ({ startOffset: edit.startOffset, endOffset: edit.endOffset }));
        deletingRanges.sort((left, right) => left.startOffset - right.startOffset);
        const merged: AcceptedTextRange[] = [];
        for (const range of deletingRanges) {
            const previous = merged[merged.length - 1];
            /**
             * CASES
             *   - it starts at or before the previous range's end -> one range, ending at the later end
             *   - it starts after a gap                           -> a range of its own
             */
            if (previous !== undefined && range.startOffset <= previous.endOffset) previous.endOffset = Math.max(previous.endOffset, range.endOffset);
            else merged.push(range);
        }
        return merged;
    }

    /**
     * Every complete complex field of the paragraph, in the order their ends are met
     * (`complexFieldWalkOf`): a field whose begin or end lies in another paragraph is
     * no link this paragraph can strike whole.
     */
    private static completeComplexFieldsOf(paragraph: Element): ComplexField[] {
        return complexFieldWalkOf(paragraph).fields.filter((field) => field.complete);
    }

    /**
     * Takes a link's runs out of the accepted text: each struck as this author's
     * deletion, or removed where it is the author's own pending insertion.
     *
     * CASES, per run
     *   - struck already, by anyone            -> left as it is: a second wrapper would be a deletion of a deletion
     *   - this author's own pending insertion   -> removed outright: nothing of it was ever accepted, as the
     *                                             engine removes the author's own inserted text everywhere
     *   - any other run                         -> struck as this author's deletion
     */
    private static strikeOrRemoveRuns(linkRuns: readonly Element[], paragraph: Element, revision: Revision): void {
        for (const linkRun of linkRuns) {
            if (LinkFieldStriker.sitsInsideDeletion(linkRun, paragraph)) continue;
            if (LinkFieldStriker.sitsInsideInsertionBy(linkRun, paragraph, revision)) linkRun.parentNode?.removeChild(linkRun);
            else ParagraphRevisionMarks.strikeRun(linkRun, revision);
        }
        LinkFieldStriker.removeInsertionsLeftEmpty(paragraph);
    }

    /**
     * Rewrites a `w:fldSimple` as the complex spelling of the same field, in its
     * place: a `fldChar begin` run (holding the field's `w:fldData`, where it has
     * one), an instruction run, a `fldChar separate` run, the field's own result
     * runs moved, not rebuilt, and a `fldChar end` run.
     *
     * WHY
     *   No `w:del` may hold a `w:fldSimple`, so a simple-field link cannot be struck
     *   whole as it stands, and unwrapping it lost the link on Reject All. Both
     *   spellings are one field to Word; in the complex one every piece is a run,
     *   which a `w:del` holds, so the link is struck as Word strikes a link.
     *
     * EXAMPLE
     *   <w:fldSimple w:instr=' HYPERLINK "https://x.example/fee" '><w:r>the Fee Schedule</w:r></w:fldSimple>
     *   -> [begin] [instrText ' HYPERLINK "https://x.example/fee" '] [separate] <w:r>the Fee Schedule</w:r> [end]
     *
     * RETURNS
     *   The field's new begin run, the element that opens the link from now on.
     *
     * NULL
     *   A detached element, left as it was.
     */
    private static rewriteAsComplexField(fieldSimple: Element): Element | null {
        const parent = fieldSimple.parentNode;
        /** A detached element has no place to be rewritten in. */
        if (parent === null) return null;
        const markerRuns = LinkFieldStriker.complexFieldMarkerRuns(fieldSimple.ownerDocument as Document, fieldSimpleInstructionOf(fieldSimple));
        parent.insertBefore(markerRuns.beginRun, fieldSimple);
        parent.insertBefore(markerRuns.instructionRun, fieldSimple);
        parent.insertBefore(markerRuns.separateRun, fieldSimple);
        for (const child of Array.from(fieldSimple.childNodes)) {
            /** The field's own data rides in its begin marker in the complex spelling. */
            if (child.nodeType === 1 && elementNameWithoutPrefix(child as Element) === "fldData") markerRuns.beginFieldChar.appendChild(child);
            else parent.insertBefore(child, fieldSimple);
        }
        parent.insertBefore(markerRuns.endRun, fieldSimple);
        parent.removeChild(fieldSimple);
        return markerRuns.beginRun;
    }

    /**
     * Rewrites a `w:hyperlink` element as the complex HYPERLINK field of the same
     * address, in its place, and returns the field's runs begin to end: the begin,
     * instruction and separate marker runs, the element's own runs moved, not
     * rebuilt, with whatever wraps them (another author's `w:ins`, a bookmark),
     * and the end marker run.
     *
     * WHY
     *   No `w:del` may hold a `w:hyperlink`, and an element left around struck runs
     *   stays in the document after Accept All as an empty link (Word probe 2,
     *   2026-10-16). Both spellings are one link to Word; in the complex one every
     *   piece is a run, so the link is struck as Word strikes a link: gone once
     *   accepted, back with its address once rejected.
     *
     * EXAMPLE
     *   <w:hyperlink r:id="rId11"><w:r>the Fee Schedule</w:r></w:hyperlink>, rId11 -> https://x.example/fee
     *   -> [begin] [instrText ' HYPERLINK "https://x.example/fee" '] [separate] <w:r>the Fee Schedule</w:r> [end]
     *
     * EDGE CASE
     *   A detached element has no place to be rewritten in: its own runs come back,
     *   struck inside it as before.
     */
    private static rewriteHyperlinkElementAsComplexField(hyperlinkElement: Element, fieldInstruction: string): Element[] {
        const parent = hyperlinkElement.parentNode;
        /** A detached element: its runs are struck inside it. */
        if (parent === null) return Array.from(hyperlinkElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "r"));
        const markerRuns = LinkFieldStriker.complexFieldMarkerRuns(hyperlinkElement.ownerDocument as Document, fieldInstruction);
        const linkRuns = Array.from(hyperlinkElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "r"));
        parent.insertBefore(markerRuns.beginRun, hyperlinkElement);
        parent.insertBefore(markerRuns.instructionRun, hyperlinkElement);
        parent.insertBefore(markerRuns.separateRun, hyperlinkElement);
        for (const child of Array.from(hyperlinkElement.childNodes)) parent.insertBefore(child, hyperlinkElement);
        parent.insertBefore(markerRuns.endRun, hyperlinkElement);
        parent.removeChild(hyperlinkElement);
        return [markerRuns.beginRun, markerRuns.instructionRun, markerRuns.separateRun, ...linkRuns, markerRuns.endRun];
    }

    /**
     * The HYPERLINK field instruction that writes the element's link, or null
     * when its address is not known.
     *
     * CASES
     *   - `r:id` found in `hyperlinkTargets`     -> ' HYPERLINK "<target>" ', with ' \l "<anchor>" ' for a
     *                                              `w:anchor` beside it (a place inside the target)
     *   - no `r:id`, a `w:anchor`                -> ' HYPERLINK \l "<anchor>" ': a link to a bookmark of
     *                                              this document
     *   - an `r:id` the targets do not hold      -> null: the address cannot be written, and an anchor
     *                                              alone would point the link somewhere else
     *   - neither                                -> null
     *   - a `w:tooltip`                          -> ' \o "<tooltip>" ' added, so Reject brings the tip back
     *   - a `w:tgtFrame`                         -> ' \t "<frame>" ' added, so a link that opened in a new
     *                                              window ("_blank") still does once rejected
     *
     * EXAMPLE
     *   r:id "rId11" -> "https://x.example/fee", w:tooltip "Fees" -> ' HYPERLINK "https://x.example/fee" \o "Fees" '
     *   w:anchor "Schedule_1"                                     -> ' HYPERLINK \l "Schedule_1" '
     *   r:id "rId11", w:tgtFrame "_blank"                          -> ' HYPERLINK "https://x.example/fee" \t "_blank" '
     */
    private static hyperlinkFieldInstructionOf(hyperlinkElement: Element, hyperlinkTargets: HyperlinkTargetsByRelationshipId): string | null {
        const { relationshipId, placeInTarget: anchor, tooltip, targetFrame } = hyperlinkElementAttributesOf(hyperlinkElement);
        /**
         * CASES
         *   - the element names a relationship -> its target, or null when the package does not hold it
         *   - it names none (a bookmark link)  -> null
         */
        const target = relationshipId === null ? null : hyperlinkTargets.get(relationshipId) ?? null;
        /** A relationship the package does not hold: the address cannot be written. */
        if (relationshipId !== null && target === null) return null;
        /** Neither an address nor a bookmark: there is no link to write. */
        if (target === null && anchor === null) return null;
        const instructionParts = ["HYPERLINK"];
        /** An external or package address leads the instruction. */
        if (target !== null) instructionParts.push(LinkFieldStriker.fieldArgument(target));
        /** A place inside the target, or the bookmark the link goes to. */
        if (anchor !== null) instructionParts.push("\\l", LinkFieldStriker.fieldArgument(anchor));
        /** The tip Word shows over the link. */
        if (tooltip !== null) instructionParts.push("\\o", LinkFieldStriker.fieldArgument(tooltip));
        /** The window or frame the link opens in. */
        if (targetFrame !== null) instructionParts.push("\\t", LinkFieldStriker.fieldArgument(targetFrame));
        return ` ${instructionParts.join(" ")} `;
    }

    /**
     * A field argument in Word's quoting: wrapped in double quotes, a backslash
     * or a double quote inside it escaped with a backslash.
     *
     * EXAMPLE
     *   https://x.example/a"b -> "https://x.example/a\"b"
     */
    private static fieldArgument(argumentText: string): string {
        return `"${argumentText.replace(/[\\"]/g, (character) => `\\${character}`)}"`;
    }

    /**
     * The four marker runs of a complex field around its result: `fldChar begin`,
     * the instruction as `w:instrText`, `fldChar separate`, `fldChar end`, each in
     * a run of its own, and the begin `fldChar` itself, which carries a simple
     * field's `w:fldData` once rewritten.
     */
    private static complexFieldMarkerRuns(ownerDocument: Document, fieldInstruction: string): ComplexFieldMarkerRuns {
        const runHolding = (runChild: Element): Element => {
            const markerRun = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:r");
            markerRun.appendChild(runChild);
            return markerRun;
        };
        const fieldChar = (fieldCharType: string): Element => {
            const fieldCharElement = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:fldChar");
            fieldCharElement.setAttributeNS(WORDPROCESSINGML_NS, "w:fldCharType", fieldCharType);
            return fieldCharElement;
        };
        const beginFieldChar = fieldChar("begin");
        const instructionText = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:instrText");
        instructionText.setAttributeNS(XML_NS, "xml:space", "preserve");
        instructionText.textContent = fieldInstruction;
        return {
            beginFieldChar,
            beginRun: runHolding(beginFieldChar),
            instructionRun: runHolding(instructionText),
            separateRun: runHolding(fieldChar("separate")),
            endRun: runHolding(fieldChar("end")),
        };
    }

    /**
     * Whether the run sits inside a `w:ins` by this revision's author below the
     * paragraph: text the author inserted and nobody has accepted yet.
     */
    private static sitsInsideInsertionBy(run: Element, paragraph: Element, revision: Revision): boolean {
        for (let ancestor = run.parentElement; ancestor !== null && ancestor !== paragraph; ancestor = ancestor.parentElement) {
            if (elementNameWithoutPrefix(ancestor) === "ins" && revision.isBy(ancestor.getAttributeNS(WORDPROCESSINGML_NS, "author") ?? ancestor.getAttribute("w:author"))) return true;
        }
        return false;
    }

    /**
     * Removes every `w:ins` of the paragraph that holds no element any more, the
     * shell an own insertion leaves once its runs are removed.
     *
     * EXAMPLE
     *   <w:ins w:author="Dioptra AI"/>                    -> removed
     *   <w:ins …><w:r>…</w:r></w:ins>                      -> kept
     *   <w:pPr><w:rPr><w:ins …/></w:rPr></w:pPr>          -> kept: the paragraph mark's insertion record
     *   <w:pPr><w:numPr>…<w:ins …/></w:numPr></w:pPr>     -> kept: numbering added with tracking on
     */
    private static removeInsertionsLeftEmpty(paragraph: Element): void {
        for (const insertion of Array.from(paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "ins"))) {
            /** An insertion inside a property block is a revision record, never a container of runs. */
            if (LinkFieldStriker.sitsInsidePropertyBlock(insertion, paragraph)) continue;
            if (insertion.children.length === 0) insertion.parentNode?.removeChild(insertion);
        }
    }

    /**
     * Whether the element sits under a `w:pPr` or a `w:rPr` below the paragraph:
     * paragraph or run properties, where a `w:ins` records a change to the
     * properties (the paragraph mark, numbering added with tracking on) and never
     * holds runs.
     */
    private static sitsInsidePropertyBlock(element: Element, paragraph: Element): boolean {
        for (let ancestor = element.parentElement; ancestor !== null && ancestor !== paragraph; ancestor = ancestor.parentElement) {
            const ancestorName = elementNameWithoutPrefix(ancestor);
            if (ancestorName === "pPr" || ancestorName === "rPr") return true;
        }
        return false;
    }

    /** Whether the run sits inside a `w:del` or `w:moveFrom` below the paragraph. */
    private static sitsInsideDeletion(run: Element, paragraph: Element): boolean {
        for (let ancestor = run.parentElement; ancestor !== null && ancestor !== paragraph; ancestor = ancestor.parentElement) {
            const ancestorName = elementNameWithoutPrefix(ancestor);
            if (ancestorName === "del" || ancestorName === "moveFrom") return true;
        }
        return false;
    }
}
